/**
 * Spec module — Phase 4 Slice 4B store.
 *
 * Mirrors `frontend/src/store/drawingStore.ts` shape:
 *   - hydration status state machine
 *   - matter-scoped server state (spec_document, sections,
 *     paragraphsBySection, examiner flags, feature mappings,
 *     reference signs, drawing summaries)
 *   - local UI state (active section / paragraph, rail collapses,
 *     dock open/closed map, loading, error)
 *   - sticky preferences persisted to localStorage (active section
 *     id, rail collapses, dock open map)
 *
 * No auto-save in Phase 4 — every mutation lands in Phase 5+.
 */
import { create } from 'zustand';
import {
  enqueueOfflineEntry,
  loadOfflineQueue,
  removeOfflineEntries,
  type SpecOfflineQueue,
  type SpecOfflineQueueEntry,
} from '@/lib/specOfflineQueue';
import {
  deleteSpecParagraph,
  getSpecDrawingSummaries,
  getSpecExaminerFlags,
  getSpecFeatureMappings,
  getSpecReferenceSigns,
  getSpecSectionDetail,
  getSpecSectionRender,
  getSpecSections,
  getSpecState,
  resetSpecSectionParagraphs,
  resetAllSpecParagraphs,
  cancelSpecGenerateEverything,
  getSpecGenerateEverythingStatus,
  listSpecGenerateEverythingRuns,
  getSpecGlossary,
  getSpecTerms,
  type SpecTermWebEntry,
  getSpecIdfCoverage,
  getSpecLastIdfCoverage,
  type SpecIdfCoverageResponse,
  getSpecRevisionProposals,
  patchSpecFeatureMapping,
  patchSpecRevisionProposal,
  postSpecExaminerReview,
  postSpecFlagAcknowledge,
  postSpecFlagDismiss,
  postSpecFlagResolve,
  postSpecGenerateEverything,
  postSpecRevisionProposal,
  postSpecRevisionProposalMarkConsumed,
  postSpecSectionVerify,
  patchSpecParagraph,
  postSpecParagraphCreate,
  postSpecParagraphExpand,
  postSpecParagraphTighten,
  patchSpecParagraphLock,
  postSpecSectionGenerate,
  postSpecSectionNlEdit,
  postSpecSectionReplaceParagraphs,
  postSpecSeed,
  type SpecDocument,
  type SpecDrawingSummary,
  type SpecExaminerFlag,
  type SpecFeatureMapping,
  type SpecNlEditResponse,
  type SpecExaminerReviewSummary,
  type SpecGenerateEverythingRun,
  type SpecGenerateEverythingStatus,
  type SpecGlossaryEntry,
  type SpecParagraph,
  type SpecParagraphExpandResponse,
  type SpecParagraphTightenResponse,
  type SpecReferenceSign,
  type SpecRenderResponse,
  type SpecRevisionProposal,
  type SpecRevisionProposalCreateBody,
  type SpecSectionVerifyIssue,
  type SpecSectionGenerateResponse,
  type SpecSectionRow,
  type SpecSectionSummary,
  type SpecStateResponse,
  type SpecGenerationFindings,
} from '@/api/client';

/** The finding lists that are non-empty, or null when there is
 * nothing to show (NB-17 / NB-28 / NB-29 / NB-36). */
export function pickFindings(
  meta: SpecGenerationFindings | null | undefined,
): SpecGenerationFindings | null {
  if (!meta) return null;
  const unsupported = meta.unsupported_statements || [];
  const figures = meta.figure_mismatches || [];
  const renamed = meta.numeral_label_mismatches || [];
  const kept = meta.validation_warnings || [];
  if (unsupported.length + figures.length + renamed.length + kept.length === 0) {
    return null;
  }
  return {
    unsupported_statements: unsupported,
    figure_mismatches: figures,
    numeral_label_mismatches: renamed,
    validation_warnings: kept,
  };
}

export type SpecHydrationStatus =
  | 'idle'
  | 'loading'
  | 'unlocked'
  | 'locked'
  | 'error';

export type DockKey =
  | 'frozenClaims'
  | 'frozenDrawings'
  | 'referenceSigns'
  | 'featureMap'
  | 'examinerView'
  | 'glossary'
  /** BUG-039 — live IDF coverage audit dock. */
  | 'idfCoverage'
  /** Term/Phrase web — human-like spec generation curation. */
  | 'termWeb';

const ALL_DOCK_KEYS: DockKey[] = [
  'frozenClaims',
  'frozenDrawings',
  'referenceSigns',
  'featureMap',
  'examinerView',
  'glossary',
  'idfCoverage',
  'termWeb',
];

const STORAGE_PREFIX = 'spec:';
const STORAGE_KEY_ACTIVE_SECTION = (matterId: string) =>
  `${STORAGE_PREFIX}activeSectionId:${matterId}`;
const STORAGE_KEY_LEFT_COLLAPSED = `${STORAGE_PREFIX}leftCollapsed`;
const STORAGE_KEY_RIGHT_COLLAPSED = `${STORAGE_PREFIX}rightCollapsed`;
const STORAGE_KEY_DOCKS_OPEN = `${STORAGE_PREFIX}docksOpen`;

function _readBool(key: string, fallback: boolean): boolean {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === 'true') return true;
    if (raw === 'false') return false;
  } catch {
    /* localStorage unavailable */
  }
  return fallback;
}

function _writeBool(key: string, value: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value ? 'true' : 'false');
  } catch {
    /* persistence is best-effort */
  }
}

function _readDocksOpen(): Record<DockKey, boolean> {
  // Sane default per brief §4: 5 docks open on first load (Glossary
  // closed; brief §4 says "five docks open by default").
  const fallback: Record<DockKey, boolean> = {
    frozenClaims: true,
    frozenDrawings: true,
    referenceSigns: true,
    featureMap: true,
    examinerView: true,
    glossary: false,
    // BUG-039 — IDF coverage opens by default so drafters see the
    // audit on page load; the cue calls out empty / "missing" as
    // the most actionable surface.
    idfCoverage: true,
    // Term web ships closed by default — opt-in surface until
    // the drafter starts the term-driven DD workflow.
    termWeb: false,
  };
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY_DOCKS_OPEN);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    const out = { ...fallback };
    for (const key of ALL_DOCK_KEYS) {
      if (typeof parsed?.[key] === 'boolean') out[key] = parsed[key];
    }
    return out;
  } catch {
    return fallback;
  }
}

function _writeDocksOpen(map: Record<DockKey, boolean>): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY_DOCKS_OPEN, JSON.stringify(map));
  } catch {
    /* persistence is best-effort */
  }
}

function _readActiveSection(matterId: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY_ACTIVE_SECTION(matterId));
  } catch {
    return null;
  }
}

function _writeActiveSection(matterId: string, sectionId: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (sectionId) {
      window.localStorage.setItem(
        STORAGE_KEY_ACTIVE_SECTION(matterId),
        sectionId,
      );
    } else {
      window.localStorage.removeItem(STORAGE_KEY_ACTIVE_SECTION(matterId));
    }
  } catch {
    /* persistence is best-effort */
  }
}

function _isLocked<T>(payload: T | SpecStateResponse): payload is SpecStateResponse {
  return (
    !!payload &&
    typeof payload === 'object' &&
    (payload as SpecStateResponse).locked === true
  );
}

/** Debounce window for paragraph auto-save (Slice 5E). 600ms is
 * the spec-side default (vs drawing's 400ms) — text editing is
 * burstier so a longer window reduces server thrash. Keep the
 * two modules' values different deliberately per
 * SPEC_BUILD_INDEX decision #5. */
export const PARAGRAPH_DEBOUNCE_MS = 600;

/** Auto-save status for the header indicator. */
export type SpecParagraphSaveStatus = 'idle' | 'saving' | 'error';

/** Status of an in-flight section-generate call. */
export type SpecGenerateStatus = 'idle' | 'generating' | 'error';

/** Status of an in-flight paragraph mutation (expand/tighten). */
export type SpecParagraphMutationStatus =
  | 'idle' | 'expanding' | 'tightening' | 'error';

/** Status of an in-flight NL-edit call. */
export type SpecNlEditStatus = 'idle' | 'pending' | 'error';

/** Outcome of a paragraph mutation (expand/tighten). */
export type SpecParagraphMutationOutcome =
  | { kind: 'ok'; paragraph: SpecParagraph; charDiff: number }
  | { kind: 'locked'; reason: string }
  | { kind: 'error'; message: string; errorCode?: string };

/** Outcome of an NL-edit. */
export type SpecNlEditOutcome =
  | { kind: 'ok'; response: SpecNlEditResponse }
  | { kind: 'locked'; reason: string }
  | { kind: 'error'; message: string; errorCode?: string };

/** Outcome of a generateSection call. ``confirm_overwrite`` is the
 * cooperative branch the UI uses to surface the overwrite-confirm
 * modal. ``ok`` and ``error`` are terminal. */
export type SpecGenerateOutcome =
  | { kind: 'ok'; response: SpecSectionGenerateResponse }
  | { kind: 'confirm_overwrite'; sectionId: string; paragraphCount: number }
  | { kind: 'locked'; reason: string }
  | { kind: 'error'; message: string; errorCode?: string };

/** Section-level pending diff: a snapshot of the section's
 *  paragraphs captured right before the most recent regenerate,
 *  plus the steering text (drafter_intent) that drove it. The
 *  SectionDiffPanel reads this to render a side-by-side compare
 *  and to power the "Revert to previous version" action. Cleared
 *  on dismiss, on successful revert, when the section is frozen,
 *  or when the drafter regenerates again (a new snapshot
 *  supersedes the old one). */
export interface SpecSectionPendingDiff {
  /** Pre-regenerate paragraphs (sorted by sequence_num). Each
   *  carries body_html / body_text / paragraph_number / sequence_num
   *  — the exact rows the bulk-replace endpoint replays back into
   *  the section on revert. */
  previous: SpecParagraph[];
  /** Drafter_intent that drove the regenerate, or null when the
   *  drafter regenerated without typing steering. Surfaced in the
   *  banner so the drafter can see "this is the result of XYZ
   *  instruction" before deciding to keep or revert. */
  steering: string | null;
  /** Epoch ms — used to age out the diff after the next page
   *  reload (snapshots don't survive a refresh; see
   *  resetForMatter). */
  timestamp: number;
}

interface SpecStore {
  // ── Hydration status ─────────────────────────────────────────
  hydrationStatus: SpecHydrationStatus;
  lockedReason: string | null;
  errorMessage: string | null;
  hydratedMatterId: string | null;

  // ── Server state ─────────────────────────────────────────────
  matterId: string | null;
  specDocument: SpecDocument | null;
  sectionSummary: SpecSectionSummary | null;
  sections: SpecSectionRow[];
  paragraphsBySection: Record<string, SpecParagraph[]>;
  /** BUG-015 — server-side content-replacement counter per
   * paragraph_id. Bumped by every store action that replaces a
   * paragraph's body via an LLM call (expand / tighten / NL-edit
   * / AI-suggest popover). NOT bumped by local typing
   * (updateParagraphLocalAndQueueSave) — ParagraphBlock's
   * imperative innerHTML reset depends on this so the DOM
   * reconciles with server replacements without the cursor
   * jumping mid-type. */
  paragraphServerVersion: Record<string, number>;
  examinerFlags: SpecExaminerFlag[];
  featureMappings: SpecFeatureMapping[];
  revisionProposals: SpecRevisionProposal[];
  referenceSigns: SpecReferenceSign[];
  drawingSummaries: SpecDrawingSummary[];

  // ── Local UI state ───────────────────────────────────────────
  activeSectionId: string | null;
  activeParagraphId: string | null;
  leftCollapsed: boolean;
  rightCollapsed: boolean;
  docksOpen: Record<DockKey, boolean>;
  toastMessage: string | null;

  // ── Generate-section state (Slice 5D) ────────────────────────
  generateStatus: SpecGenerateStatus;
  generateError: string | null;
  /** When the server returns 409 section_not_empty, the UI shows
   * an overwrite-confirm modal; the section + count are surfaced
   * here so the modal renders without an extra fetch. Cleared by
   * confirmGenerateOverwrite / cancelGenerateOverwrite. */
  pendingOverwrite:
    | { sectionId: string; paragraphCount: number; drafterIntent: string }
    | null;

  /** Set to true when the drafter clicked "Add coverage for these
   *  features →" on the claim-anchor warning banner. Two effects:
   *    1. ActionBar's Regenerate button shows a visual indicator
   *       so the drafter sees that a steering payload is loaded
   *       and ready.
   *    2. Clicking Regenerate while this is true SKIPS the
   *       overwrite-confirm dialog and goes straight to the
   *       overwrite=true generate call — the drafter explicitly
   *       opted in by clicking the banner CTA.
   *  Cleared after the resulting generate completes (success or
   *  error) or when the drafter cancels the action otherwise. */
  pendingRegenerateWithSteering: boolean;

  // ── Paragraph auto-save state (Slice 5E) ─────────────────────
  paragraphSaveStatus: SpecParagraphSaveStatus;
  paragraphSaveError: string | null;
  /** Timestamp of the last successful auto-save. Surfaced in the
   * header as "auto-saved · HH:MM". */
  lastSavedAt: string | null;
  /** Number of paragraph saves currently in flight. UI uses this
   * + paragraphSaveStatus to render the "saving…" chip. */
  pendingSaveCount: number;

  // ── Paragraph mutation state (Slice 6D) ──────────────────────
  /** State machine for in-flight expand/tighten on a paragraph.
   * UI dims the active paragraph + disables hover rails during
   * 'expanding' / 'tightening'. */
  paragraphMutationStatus: SpecParagraphMutationStatus;
  paragraphMutationError: string | null;
  /** Paragraph being expanded/tightened — UI scopes the spinner
   * to this row. */
  paragraphMutationTargetId: string | null;

  // ── NL-edit state (Slice 6D) ────────────────────────────────
  nlEditStatus: SpecNlEditStatus;
  nlEditError: string | null;
  /** Last NL-edit changes_summary string for the toast surface. */
  lastNlEditSummary: string | null;
  /** Warnings from the most recent NL-edit (refused requests,
   * structural mismatches). UI surfaces above the prompt bar. */
  lastNlEditWarnings: string[];

  // ── Phase 9 Slice 9B — revision proposals + glossary ─────────
  /** Loaded glossary terms for the matter (from Stage 1
   * Technology Basics). Populated lazily by loadGlossary. */
  glossaryEntries: SpecGlossaryEntry[];
  glossaryStatus: 'idle' | 'loading' | 'error';
  /** Term/Phrase web (docs/SPEC_TERM_WEB_DESIGN.md). Ordered
   * inventory of claim Terms + Phrases driving the 6-sentence
   * chained Detailed Description generation. Loaded lazily by
   * loadTermWeb; TermWebDock mutates via direct API calls then
   * refreshes with loadTermWeb(true). */
  termWeb: SpecTermWebEntry[];
  termWebStatus: 'idle' | 'loading' | 'error';
  /** BUG-039 — live IDF coverage panel data. ``status`` mirrors the
   * glossary pattern; ``data`` is the full server payload (counts,
   * concepts, last_checked) so the dock can render without
   * re-fetching after a section save. */
  idfCoverage: SpecIdfCoverageResponse | null;
  idfCoverageStatus: 'idle' | 'loading' | 'error';

  /** Post-generation claim-anchor coverage warnings. Keyed by
   * section.id; ``missing`` is the list of feature tags the
   * model didn't anchor (e.g. ["F2", "F5"]); ``dependent`` is
   * whether the check pulled in dependent-claim features (drives
   * the banner's scope copy: detailed-description vs. summary).
   * Set on generate / GE completion, cleared on dismiss /
   * freeze / regenerate. */
  anchorWarnings: Record<string, { missing: string[]; dependent: boolean }>;

  /** Per-section invented-reference-sign warning. Keyed by
   * section.id; ``numerals`` is what the model used but the
   * matter's registry doesn't have, ``where`` maps each to the
   * paragraph number it first appears in. Set on generate / GE
   * completion, cleared on dismiss / freeze / regenerate — same
   * lifecycle as anchorWarnings. */
  numeralWarnings: Record<
    string, { numerals: string[]; where: Record<string, string> }
  >;

  /** NB-11 — per-section note of generated text that was cut because
   * it cited a figure that is not frozen. Same lifecycle as
   * numeralWarnings. */
  figureWarnings: Record<string, { figures: string[]; excerpts: string[] }>;

  /** NB-17 / NB-28 / NB-29 — per-section findings from the last
   * generate (or Generate everything): statements the disclosure does
   * not support, figures cited for numerals they do not show,
   * paragraphs kept with a problem. Same lifecycle as figureWarnings. */
  findingWarnings: Record<string, SpecGenerationFindings>;

  /** Per-section pending diff (post-regenerate compare). Keyed
   *  by section.id; absent when no diff is pending. Populated by
   *  ``generateSection`` (success branch snapshots the prior
   *  paragraphs) and cleared by dismissDiff, revertSectionToSnapshot,
   *  freezeSection, or the next regenerate. */
  pendingDiffBySection: Record<string, SpecSectionPendingDiff>;
  /** In-flight proposal submission marker — modal disables
   * Submit while running. */
  proposalSubmitStatus: 'idle' | 'submitting' | 'error';

  // ── Phase 9 Slice 9C — drawing pop-out viewer ────────────────
  /** Single-instance drawing pop-out: when non-null, the
   * floating panel is rendered at SpecPage level. Setting a
   * different id closes the prior pop-out (memory bound). */
  popoutFigureId: string | null;

  // ── Phase 11 Slice 11D — Generate Everything ─────────────────
  /** Currently-running Generate-Everything job (if any). The
   * polling loop in startGenerateEverything keeps the status
   * fresh; reaches a terminal state ('completed'/'failed'/
   * 'cancelled') and stops polling. */
  generateEverythingJobId: string | null;
  generateEverythingStatus: SpecGenerateEverythingStatus | null;
  /** Most recent terminal-state failed-section list — surfaces
   * in the post-completion banner. Cleared when the drafter
   * dismisses the banner or starts a new job. */
  generateEverythingFailures: Array<{
    section_kind: string; error_code?: string; message?: string;
  }>;
  /** Status snapshot of the cap pre-flight 429, when triggered.
   * The dialog surfaces it inline instead of trying to start
   * the job. */
  generateEverythingCapBlocked: null | {
    message: string; reset_at?: string; cap?: number; spend?: number;
  };
  /** Persistent list of recent generate-everything runs for the
   * active matter — hydrated on SpecPage mount and refreshed when
   * a job reaches terminal status. The header history popover and
   * the resume-on-mount path both read from here so terminal runs
   * never disappear from the drafter's view. */
  generateEverythingRuns: SpecGenerateEverythingRun[];

  // ── Feature drag-anchor state (Slice 8D) ─────────────────────
  /** Most recently anchored paragraph id; the receiving
   * ParagraphBlock listens for this and applies a 1 s
   * s6-para-feature-drop pulse, then the store auto-clears
   * the marker after the animation finishes. */
  lastAnchoredParagraphId: string | null;

  // ── Examiner / verify state (Slice 8C) ───────────────────────
  /** Status of the in-flight matter-level examiner review. The
   * dock spinner + Verify button respect this. */
  examinerStatus: 'idle' | 'running' | 'error';
  examinerError: string | null;
  examinerSummary: SpecExaminerReviewSummary | null;
  /** Bumped when an Examiner run finishes, so the Examiner view dock
   * opens and scrolls itself into view: its results used to land in
   * the sixth dock of the right rail, below the fold, and the run
   * looked as if it had surfaced nothing. */
  examinerRevealNonce: number;
  /** Per-section transient verify issues (NOT persisted server-side
   * by spec_section_verify; the store keeps them until the user
   * re-verifies, regenerates, or hard-reloads). Keyed by section_id. */
  sectionVerifyIssues: Record<string, SpecSectionVerifyIssue[]>;
  sectionVerifyStatus: Record<string, 'idle' | 'running' | 'error'>;
  sectionVerifyScore: Record<string, { completeness_pct: number; issue_count: number }>;

  // ── Auto-section render cache (Slice 7D) ─────────────────────
  /** Last successfully fetched render payload per auto-section.
   * Survives view switches; refreshed via loadSectionRender when
   * the user reopens the section or clicks "Refresh". */
  renderBySection: Record<string, SpecRenderResponse>;
  /** ETag echoed back as If-None-Match on the next fetch so a
   * 304 short-circuits the body roundtrip. */
  renderEtagBySection: Record<string, string>;
  renderStatusBySection: Record<
    string,
    'idle' | 'loading' | 'error'
  >;
  renderErrorBySection: Record<string, string | null>;

  // ── Offline queue state (Slice 6F) ───────────────────────────
  /** True while the auto-save PATCH path is failing with network
   * errors and entries are accumulating in the IDB queue. UI
   * surfaces the "Working offline · N changes queued" banner. */
  offlineMode: boolean;
  /** Number of paragraphs currently in the offline queue. */
  pendingOfflineCount: number;

  // ── Actions ──────────────────────────────────────────────────
  hydrate: (matterId: string) => Promise<void>;
  loadActiveSectionParagraphs: (sectionId: string) => Promise<void>;
  /** Phase 12E.1 — two-tab last-write-wins. Re-fetches the active
   * section's paragraphs + reference_signs + drawingSummaries on
   * window.focus. If the active section's server-side ``updated_at``
   * is newer than the cached one, fires the "Section updated in
   * another tab" toast + replaces local cache. Best-effort: any
   * fetch failure is logged + swallowed. */
  refreshOnFocus: () => Promise<void>;
  /** Lightweight refetch fired when SpecPage remounts for the
   *  same matter (in-tab nav Spec → Claims → Spec). Re-pulls
   *  the section list + section_summary so a background GE that
   *  completed while Spec was unmounted is reflected on the
   *  rail. Cheaper than a full ``hydrate`` (one /sections call,
   *  one /state call) so it can fire on every same-matter
   *  remount without a meaningful network burden. */
  refreshSectionsForActiveMatter: () => Promise<void>;
  /** Fetch (or refresh) the render payload for an auto-generated
   * section. Sends the cached ETag as If-None-Match so a 304
   * keeps the cached payload. ``force=true`` skips the ETag and
   * always returns fresh data — used by the section's "Refresh"
   * button. */
  loadSectionRender: (sectionId: string, opts?: { force?: boolean }) => Promise<void>;
  setActiveSection: (sectionId: string | null) => void;
  /** BUG-033 — replace the sections list (used after exclude/reorder
   * PATCH so the rail re-renders without a /sections refetch). */
  setSections: (sections: SpecSectionRow[]) => void;
  // Reset paragraph content without regenerating. Per-section variant
  // refuses on frozen / auto-generated; bulk variant skips them.
  // Both clear the affected section's paragraphsBySection cache and
  // refetch the sections list so word_count + paragraph_count update.
  resetSection: (
    sectionId: string,
  ) => Promise<{ kind: 'ok'; deleted: number } | { kind: 'error'; message: string }>;
  resetAllSections: () => Promise<
    { kind: 'ok'; sections: number; deleted: number }
    | { kind: 'error'; message: string }
  >;
  setActiveParagraph: (paragraphId: string | null) => void;
  toggleLeftRail: () => void;
  toggleRightRail: () => void;
  toggleDock: (key: DockKey) => void;
  /** Imperative open/close — used by Slice 7F cross-rail handlers
   * (numeral click → ensure ExaminerViewDock is open). */
  setDockOpen: (key: DockKey, open: boolean) => void;
  showToast: (message: string | null) => void;
  resetForMatter: (matterId: string | null) => void;
  /** Slice 5D — section generate. Returns a SpecGenerateOutcome
   * the caller branches on. The store updates internal state
   * (paragraphsBySection, sections row state, activeSectionId) on
   * success; the caller drives the UI (modal open/close, toast). */
  generateSection: (args: {
    sectionId: string;
    drafterIntent?: string;
    overwrite?: boolean;
    /** 'cover_missing' appends support for the uncovered claim
     *  features instead of rewriting the section. */
    mode?: 'rewrite' | 'cover_missing';
  }) => Promise<SpecGenerateOutcome>;
  /** Confirm-overwrite branch: re-issue the generate with
   * overwrite=true using the pendingOverwrite snapshot. */
  confirmGenerateOverwrite: () => Promise<SpecGenerateOutcome>;
  cancelGenerateOverwrite: () => void;
  /** Toggle the pendingRegenerateWithSteering flag. Set to true
   *  by ClaimAnchorWarningBanner's "Add coverage for these
   *  features →" CTA; cleared by ActionBar after the resulting
   *  generate completes. */
  setPendingRegenerateWithSteering: (pending: boolean) => void;

  // ── Paragraph auto-save actions (Slice 5E) ────────────────────
  /** Optimistically update a paragraph in-store + queue a debounced
   * PATCH. Caller passes the post-edit body_html; body_text is
   * derived from the HTML by stripping tags (s6-num spans collapse
   * to their digit text via textContent). */
  updateParagraphLocalAndQueueSave: (args: {
    paragraphId: string;
    sectionId: string;
    bodyHtml: string;
    bodyText: string;
  }) => void;
  /** Force-flush every pending paragraph save (e.g. on page unload).
   * Clears each paragraph's debounce timer and fires the PATCH
   * immediately. */
  flushParagraphSaves: () => Promise<void>;

  // ── Paragraph mutation actions (Slice 6D) ────────────────────
  expandParagraph: (args: {
    paragraphId: string;
    drafterIntent?: string;
  }) => Promise<SpecParagraphMutationOutcome>;
  tightenParagraph: (args: {
    paragraphId: string;
    drafterIntent?: string;
  }) => Promise<SpecParagraphMutationOutcome>;

  // ── NL-edit action (Slice 6D) ────────────────────────────────
  applyNlEdit: (args: {
    sectionId: string;
    drafterRequest: string;
  }) => Promise<SpecNlEditOutcome>;
  clearNlEditWarnings: () => void;

  // ── Hover-rail actions (Slice 6E) ────────────────────────────
  /** Append an empty paragraph to the section. Returns the new
   * paragraph row on success so the caller can focus / scroll
   * to it. */
  addParagraphToSection: (
    sectionId: string,
    opts?: { afterParagraphId?: string },
  ) => Promise<SpecParagraph | null>;
  /** Delete a paragraph + remove from local cache. Surviving
   * paragraphs keep their numbers (EP convention). */
  deleteParagraph: (
    paragraphId: string,
  ) => Promise<{ kind: 'ok' } | { kind: 'error'; message: string }>;

  /** Toggle a paragraph's lock flag. Locked paragraphs are
   *  preserved verbatim through the next section regenerate
   *  (backend ``_persist_generated_paragraphs`` lifts them aside
   *  + re-interleaves new paragraphs around them). UI affordance:
   *  the lock icon on the paragraph hover rail. */
  toggleParagraphLock: (
    sectionId: string,
    paragraphId: string,
  ) => Promise<{ kind: 'ok'; isLocked: boolean }
             | { kind: 'locked'; reason: string }
             | { kind: 'error'; message: string }>;

  // ── Offline queue actions (Slice 6F) ─────────────────────────
  /** On reload (or first SpecPage mount), hydrate the offline
   * queue from IDB and attempt to drain it. Sets offlineMode
   * + pendingOfflineCount based on the result. */
  hydrateOfflineQueue: () => Promise<void>;
  /** Drain pending offline entries — invoked by the store on
   * 'online' window events + a 30s polling timer + after every
   * successful PATCH. Returns the number of entries flushed. */
  flushOfflineQueue: () => Promise<number>;

  // ── Examiner / verify actions (Slice 8C) ─────────────────────
  /** Slice 8A — runs spec_examiner_review for the matter. On
   * success replaces examinerFlags + examinerSummary. The
   * `force` flag bypasses the 30 s recent-run dedupe. `reveal`
   * (default true) opens the Examiner dock on the results; the
   * automatic run after Generate everything passes false. */
  runExaminerReview: (opts?: { force?: boolean; reveal?: boolean })
    => Promise<{ kind: 'ok' | 'cached' | 'locked' | 'error'; message?: string;
                 /** Open + acknowledged flags after the run. */
                 openCount?: number }>;
  /** Slice 8B — runs spec_section_verify for one section. Issues
   * land in sectionVerifyIssues[sectionId]; transient — wiped
   * on regenerate / re-verify / page reload. */
  verifySection: (sectionId: string)
    => Promise<{ kind: 'ok' | 'locked' | 'error'; message?: string }>;
  /** Slice 8B — flag transitions. Each updates examinerFlags
   * in-store on success. */
  resolveFlag: (flagId: string, resolutionNote?: string) => Promise<void>;
  dismissFlag: (flagId: string, dismissNote?: string) => Promise<void>;
  acknowledgeFlag: (flagId: string) => Promise<void>;

  /** Phase 9 Slice 9B — submit a propose-revision dialog. On
   * success surfaces the redirect URL so the caller can
   * navigate the user to Stage 3 / Stage 5. Updates
   * revisionProposals in-store. */
  submitRevisionProposal: (body: SpecRevisionProposalCreateBody)
    => Promise<{ kind: 'ok'; proposal: SpecRevisionProposal; redirectUrl: string }
              | { kind: 'locked'; reason: string }
              | { kind: 'error'; message: string; error_code?: string }>;
  /** Phase 9 Slice 9B — drafter cancelled a proposal from the
   * dock. PATCHes status='cancelled' and updates store. */
  cancelRevisionProposal: (proposalId: string) => Promise<void>;
  /** Phase 9 Slice 9B — Stage 3/5 banner mark-consumed action. */
  markRevisionProposalConsumed: (proposalId: string, resolutionNote?: string)
    => Promise<void>;
  /** Phase 9 Slice 9B — fetch glossary terms (Stage 1
   * Technology Basics) once on demand; caches in
   * glossaryEntries. */
  loadGlossary: () => Promise<void>;
  /** Term web — fetch the matter's term inventory. Cache-hit
   * no-op unless force=true. */
  loadTermWeb: (force?: boolean) => Promise<void>;
  /** BUG-039 — fetch the IDF-coverage audit. Idempotent on cache
   * hit (5 min TTL server-side); ``forceRefresh`` bypasses the
   * cache for the manual refresh button. */
  loadIdfCoverage: (forceRefresh?: boolean) => Promise<void>;
  /** NB-23 — on load, show the audit if the drafter ran it on this
   * matter before; never starts a first run. */
  loadLastIdfCoverage: () => Promise<void>;

  /** Drop the anchor-coverage warning for a section. Drafter-
   * triggered (X button on the warning banner) — the entry
   * re-populates on the next regenerate if coverage still fails. */
  dismissAnchorWarning: (sectionId: string) => void;

  /** Drop the invented-reference-sign warning for a section.
   * Drafter-triggered (X on the banner) — the entry re-populates
   * on the next regenerate if the sign is still there. */
  dismissNumeralWarning: (sectionId: string) => void;
  /** Drop the removed-figure note for a section. */
  dismissFigureWarning: (sectionId: string) => void;
  /** Drop the generation-findings note for a section. */
  dismissFindingWarning: (sectionId: string) => void;

  /** Clear the post-regenerate diff for a section (drafter clicked
   *  "Keep new version" on the diff panel, or any other UI
   *  affordance that means "accept the new output"). No server
   *  call — the snapshot just drops from store memory. */
  dismissDiff: (sectionId: string) => void;
  /** Replay a previously-captured snapshot back into the section.
   *  POSTs the bulk-replace endpoint; on success, replaces the
   *  section's paragraph cache with the server-confirmed rows and
   *  drops the pending diff. */
  revertSectionToSnapshot: (sectionId: string) => Promise<{
    kind: 'ok' | 'locked' | 'error';
    message?: string;
  }>;

  /** Phase 9 Slice 9C — open the drawing pop-out for a figure.
   * Single-instance: opening a different figure closes the
   * prior pop-out. Pass null (or call closeFigurePopout) to
   * close the panel entirely. */
  openFigurePopout: (figureId: string) => void;
  closeFigurePopout: () => void;

  /** Phase 11 Slice 11D — kick off a Generate-Everything job.
   * Fires the POST + immediately starts the polling loop.
   * Resolves with the kind tag the caller branches on:
   *   'started'      — POST returned a job_id; polling running
   *   'cap_blocked'  — pre-flight 429; caller surfaces the
   *                    blocked banner
   *   'error'        — other failure
   */
  startGenerateEverything: (
    args: { sections?: string[]; drafter_intent?: string;
            skip_examiner?: boolean; repair_short?: boolean },
  ) => Promise<{ kind: 'started' | 'cap_blocked' | 'error';
                 message?: string }>;
  /** Cancel the running job. Idempotent on already-finished jobs. */
  cancelGenerateEverything: () => Promise<void>;
  /** Clear the failures banner after the drafter dismisses it. */
  dismissGenerateEverythingFailures: () => void;
  /** Fetch the recent runs list for the active matter and resume
   * polling if any run is still queued/running server-side.
   * Called from SpecPage on mount + after a terminal status so the
   * history popover stays current. Idempotent — if a poll is
   * already in flight for the same job, it's a no-op. */
  hydrateGenerateEverythingRuns: () => Promise<void>;
  /** Drafter dismissed the persistent terminal-status card — the
   * underlying run row stays in `generateEverythingRuns`, only the
   * floating bottom-right card is hidden. */
  dismissGenerateEverythingStatusCard: () => void;

  /** Slice 8D — drag a feature mapping from the rail onto a
   * paragraph; PATCHes anchor_paragraph_id and updates
   * featureMappings + lastAnchoredParagraphId (the paragraph
   * receives a 1 s blue pulse via the post-anchor effect). */
  anchorFeature: (mappingId: string, paragraphId: string)
    => Promise<{ kind: 'ok' | 'error'; message?: string; featureTag?: string }>;
  /** Slice 8D — clear a feature mapping's anchor; row becomes
   * draggable again. */
  unanchorFeature: (mappingId: string)
    => Promise<{ kind: 'ok' | 'error'; message?: string; featureTag?: string }>;
}

export const useSpecStore = create<SpecStore>((set, get) => ({
  hydrationStatus: 'idle',
  lockedReason: null,
  errorMessage: null,
  hydratedMatterId: null,

  matterId: null,
  specDocument: null,
  sectionSummary: null,
  sections: [],
  paragraphsBySection: {},
  paragraphServerVersion: {},
  examinerFlags: [],
  featureMappings: [],
  referenceSigns: [],
  drawingSummaries: [],

  activeSectionId: null,
  activeParagraphId: null,
  leftCollapsed: _readBool(STORAGE_KEY_LEFT_COLLAPSED, false),
  rightCollapsed: _readBool(STORAGE_KEY_RIGHT_COLLAPSED, false),
  docksOpen: _readDocksOpen(),
  toastMessage: null,

  generateStatus: 'idle',
  generateError: null,
  pendingOverwrite: null,
  pendingRegenerateWithSteering: false,

  paragraphSaveStatus: 'idle',
  paragraphSaveError: null,
  lastSavedAt: null,
  pendingSaveCount: 0,

  paragraphMutationStatus: 'idle',
  paragraphMutationError: null,
  paragraphMutationTargetId: null,

  nlEditStatus: 'idle',
  nlEditError: null,
  lastNlEditSummary: null,
  lastNlEditWarnings: [],

  offlineMode: false,
  pendingOfflineCount: 0,

  examinerStatus: 'idle',
  examinerError: null,
  examinerSummary: null,
  examinerRevealNonce: 0,
  sectionVerifyIssues: {},
  sectionVerifyStatus: {},
  sectionVerifyScore: {},

  revisionProposals: [],
  glossaryEntries: [],
  glossaryStatus: 'idle',
  termWeb: [],
  termWebStatus: 'idle',
  idfCoverage: null,
  idfCoverageStatus: 'idle',
  anchorWarnings: {},
  numeralWarnings: {},
  figureWarnings: {},
  findingWarnings: {},
  pendingDiffBySection: {},
  proposalSubmitStatus: 'idle',
  popoutFigureId: null,
  generateEverythingJobId: null,
  generateEverythingStatus: null,
  generateEverythingFailures: [],
  generateEverythingCapBlocked: null,
  generateEverythingRuns: [],

  lastAnchoredParagraphId: null,

  renderBySection: {},
  renderEtagBySection: {},
  renderStatusBySection: {},
  renderErrorBySection: {},

  // ── Hydrate (Phase 4 Slice 4B) ────────────────────────────────
  // Single entry point invoked by SpecPage on mount. Branches on
  // the lock contract before parallel-fetching the rest of the
  // spec-side reads. On {locked: true}, sets locked status + skips
  // the parallel fan-out entirely.
  hydrate: async (matterId: string) => {
    set({
      matterId,
      hydrationStatus: 'loading',
      errorMessage: null,
      lockedReason: null,
    });
    try {
      let stateRes = await getSpecState(matterId);
      if (stateRes.locked) {
        set({
          hydrationStatus: 'locked',
          lockedReason: stateRes.locked_reason || 'drawings_not_frozen',
          hydratedMatterId: matterId,
        });
        return;
      }

      // Lazy-seed branch: spec_document is null → fire seed and
      // refetch state. Per brief §1 work item 3 (route guard 3).
      // SECTIONS-REPAIR — also fire seed when the document exists
      // but has zero sections (the page would otherwise render
      // "No sections. Seed has not run yet." indefinitely). The
      // backend's get_or_seed_spec_document is idempotent + heals
      // missing sections under an existing doc.
      const sectionsMissing = stateRes.spec_document !== null
        && (stateRes.section_summary?.total ?? 0) === 0;
      if (stateRes.spec_document === null || sectionsMissing) {
        const seedRes = await postSpecSeed(matterId);
        if (_isLocked(seedRes)) {
          // Race: drawings unfrozen between two reads. Surface lock.
          set({
            hydrationStatus: 'locked',
            lockedReason: seedRes.locked_reason || 'drawings_not_frozen',
            hydratedMatterId: matterId,
          });
          return;
        }
        stateRes = await getSpecState(matterId);
        if (stateRes.locked) {
          set({
            hydrationStatus: 'locked',
            lockedReason: stateRes.locked_reason || 'drawings_not_frozen',
            hydratedMatterId: matterId,
          });
          return;
        }
      }

      // Parallel fan-out for the rest of the spec-side state.
      const [
        sectionsRes,
        featureMappingsRes,
        examinerFlagsRes,
        referenceSignsRes,
        drawingSummariesRes,
        revisionProposalsRes,
      ] = await Promise.all([
        getSpecSections(matterId),
        getSpecFeatureMappings(matterId),
        getSpecExaminerFlags(matterId),
        getSpecReferenceSigns(matterId),
        getSpecDrawingSummaries(matterId),
        getSpecRevisionProposals(matterId),
      ]);

      // Each could in principle return a locked-state envelope if a
      // race unfroze drawings mid-fetch; defend by collapsing to []
      // for any unexpected envelope.
      const sections: SpecSectionRow[] = _isLocked(sectionsRes) ? [] : sectionsRes;
      const featureMappings: SpecFeatureMapping[] = _isLocked(featureMappingsRes)
        ? []
        : featureMappingsRes;
      const examinerFlags: SpecExaminerFlag[] = _isLocked(examinerFlagsRes)
        ? []
        : examinerFlagsRes;
      const referenceSigns: SpecReferenceSign[] = _isLocked(referenceSignsRes)
        ? []
        : referenceSignsRes;
      const drawingSummaries: SpecDrawingSummary[] = _isLocked(drawingSummariesRes)
        ? []
        : drawingSummariesRes;
      const revisionProposals: SpecRevisionProposal[] =
        _isLocked(revisionProposalsRes) ? [] : revisionProposalsRes;

      // Restore sticky activeSectionId, falling back to the server's
      // active_section_id, then to the first non-auto section.
      const stickyActive = _readActiveSection(matterId);
      const serverActive = stateRes.active_section_id || null;
      let activeSectionId: string | null = null;
      if (stickyActive && sections.some((s) => s.id === stickyActive)) {
        activeSectionId = stickyActive;
      } else if (serverActive && sections.some((s) => s.id === serverActive)) {
        activeSectionId = serverActive;
      } else {
        activeSectionId =
          sections.find((s) => !s.is_auto_generated)?.id ??
          sections[0]?.id ??
          null;
      }

      // Defensive client-side guard: when a stale
      // ``overall_completeness_pct`` lands at 100 but every
      // non-auto section in the loaded list has zero
      // paragraphs, the percentage is wrong (a prior bug or
      // partial reset that didn't fire the recompute hook).
      // Override the displayed value to 0 so drafters don't see
      // a "100% complete" pill on an empty matter. Server-side
      // recompute via POST /admin/spec/recompute-completeness
      // is the canonical fix; this guard just keeps the UI
      // honest until the next mutation triggers a fresh write.
      const docFromServer = stateRes.spec_document ?? null;
      const safeDoc = ((): typeof docFromServer => {
        if (!docFromServer) return null;
        if ((docFromServer.overall_completeness_pct ?? 0) <= 0) {
          return docFromServer;
        }
        const nonAuto = sections.filter((s) => !s.is_auto_generated);
        if (nonAuto.length === 0) return docFromServer;
        const allEmpty = nonAuto.every(
          (s) => (s.paragraph_count ?? 0) === 0,
        );
        if (allEmpty) {
          return { ...docFromServer, overall_completeness_pct: 0 };
        }
        return docFromServer;
      })();

      set({
        hydrationStatus: 'unlocked',
        hydratedMatterId: matterId,
        specDocument: safeDoc,
        sectionSummary: stateRes.section_summary ?? null,
        sections,
        featureMappings,
        examinerFlags,
        referenceSigns,
        drawingSummaries,
        revisionProposals,
        activeSectionId,
        renderBySection: {},
        renderEtagBySection: {},
        renderStatusBySection: {},
        renderErrorBySection: {},
      });

      if (activeSectionId) {
        const activeSection = sections.find((s) => s.id === activeSectionId);
        if (activeSection?.is_auto_generated) {
          await get().loadSectionRender(activeSectionId);
        } else {
          // Fire-and-forget: load the active section's paragraphs so
          // the paper editor has content on first paint.
          await get().loadActiveSectionParagraphs(activeSectionId);
        }
      }
    } catch (e: unknown) {
      const message =
        (e as { response?: { data?: { detail?: string } }; message?: string })
          ?.response?.data?.detail ||
        (e as { message?: string })?.message ||
        'Failed to load spec state.';
      set({
        hydrationStatus: 'error',
        errorMessage: message,
        hydratedMatterId: matterId,
      });
    }
  },

  loadActiveSectionParagraphs: async (sectionId: string) => {
    const { matterId, paragraphsBySection } = get();
    if (!matterId) return;
    // Cache-hit gate: only short-circuit when we have a NON-EMPTY
    // cached array. The previous "if paragraph_count is 0, the
    // section is genuinely empty" heuristic was unreliable: a
    // stale section row with paragraph_count=0 would pin the
    // empty cache forever even when the server actually had
    // paragraphs (the "generated badge but empty content" bug).
    // Always fetch when the cache is missing or empty — one
    // GET per section per session is cheap; correctness wins.
    const cached = paragraphsBySection[sectionId];
    if (Array.isArray(cached) && cached.length > 0) {
      return; // genuine cache hit
    }
    try {
      const res = await getSpecSectionDetail(matterId, sectionId);
      if (_isLocked(res)) return;
      set((s) => ({
        paragraphsBySection: {
          ...s.paragraphsBySection,
          [sectionId]: res.paragraphs,
        },
      }));
    } catch (e: unknown) {
      // Soft-fail: paragraph fetch errors don't break the workbench.
      const msg =
        (e as { message?: string })?.message ?? 'Section paragraphs failed to load.';
      set({ toastMessage: msg });
    }
  },

  refreshOnFocus: async () => {
    // Phase 12E.1 — two-tab last-write-wins toast + cache refresh on
    // window.focus. Re-pulls the active section detail; if any
    // paragraph's server-side ``updated_at`` is newer than the
    // cached row, surfaces the toast + replaces the local cache.
    // Also refetches reference_signs + drawingSummaries so a
    // numeral added in another tab (or the Drawing module) shows
    // up immediately on the spec page (12E.3).
    const { matterId, activeSectionId, paragraphsBySection } = get();
    if (!matterId) return;
    try {
      const [signs, summaries] = await Promise.all([
        getSpecReferenceSigns(matterId).catch(() => null),
        getSpecDrawingSummaries(matterId).catch(() => null),
      ]);
      if (signs) set({ referenceSigns: signs });
      if (summaries) set({ drawingSummaries: summaries });
    } catch {
      // Soft-fail rail data; never block the active-section refresh.
    }
    // The Description Workbench (usually its own tab) writes into the
    // detailed description. Before this, coming back here refreshed
    // only the ACTIVE section's paragraphs and never the rail, so a
    // claim accepted over there left this page showing the detailed
    // description as "empty" until a full reload — the drafting team
    // reported the text as not there at all. Refresh the section list
    // (counts, badges) and the detailed description's own paragraphs
    // whether or not it is the section on screen.
    //
    // Deliberately NOT refreshSectionsForActiveMatter: that one drops
    // the active section's paragraph cache and refetches it, which on
    // every window focus would blank the section being edited, unmount
    // its paragraph blocks (caret lost) and race the debounced save.
    // The rail rows and summary are enough here; the active section's
    // own paragraphs are reconciled below, as they always were.
    try {
      const [stateRes, sectionsRes] = await Promise.all([
        getSpecState(matterId),
        getSpecSections(matterId),
      ]);
      if (!stateRes.locked && !_isLocked(sectionsRes)) {
        set({
          sections: sectionsRes,
          sectionSummary: stateRes.section_summary ?? null,
        });
      }
    } catch {
      // Soft-fail; the rail keeps what it has.
    }
    const doe = get().sections.find(
      (s) => s.section_kind === 'detailed_desc_embodiments',
    );
    if (doe && doe.id !== activeSectionId) {
      try {
        const res = await getSpecSectionDetail(matterId, doe.id);
        if (!_isLocked(res)) {
          set((s) => {
            // A paragraph block already mounted for the detailed
            // description (continuous scroll) keeps its own innerHTML
            // until its server version changes, so bump the version
            // of every paragraph whose body is newer than the cache —
            // the same reconcile the active section gets below.
            const cachedById = new Map(
              (s.paragraphsBySection[doe.id] ?? []).map((p) => [p.id, p]),
            );
            const nextVersion = { ...s.paragraphServerVersion };
            for (const p of res.paragraphs) {
              const prev = cachedById.get(p.id);
              if (!prev || (p.updated_at && p.updated_at !== prev.updated_at)) {
                nextVersion[p.id] = (nextVersion[p.id] ?? 0) + 1;
              }
            }
            return {
              paragraphsBySection: {
                ...s.paragraphsBySection,
                [doe.id]: res.paragraphs,
              },
              paragraphServerVersion: nextVersion,
            };
          });
        }
      } catch {
        // Soft-fail; the next focus tick repairs.
      }
    }
    if (!activeSectionId) return;
    try {
      const res = await getSpecSectionDetail(matterId, activeSectionId);
      if (_isLocked(res)) return;
      const cached = paragraphsBySection[activeSectionId] ?? [];
      const cachedMaxUpdated = cached.reduce<string>(
        (acc, p) => (p.updated_at && p.updated_at > acc ? p.updated_at : acc),
        '',
      );
      const serverMaxUpdated = res.paragraphs.reduce<string>(
        (acc, p) => (p.updated_at && p.updated_at > acc ? p.updated_at : acc),
        '',
      );
      // Only toast when we have a cached state to compare AND the
      // server is strictly newer. The first-load case (cached='')
      // doesn't fire — that's a normal hydration, not a two-tab
      // collision.
      if (cachedMaxUpdated && serverMaxUpdated && serverMaxUpdated > cachedMaxUpdated) {
        // BUG-015 — server is genuinely newer (two-tab last-write
        // wins case). Bump server-version for every paragraph so
        // ParagraphBlock re-reconciles the DOM, same mechanism
        // used for expand / tighten / NL-edit.
        set((s) => {
          const nextVersion = { ...s.paragraphServerVersion };
          for (const p of res.paragraphs) {
            nextVersion[p.id] = (nextVersion[p.id] ?? 0) + 1;
          }
          // Spread the store's CURRENT map, not the one read at the
          // top of this function: the detailed description was just
          // refreshed above, and the old snapshot would put its stale
          // copy back.
          return {
            paragraphsBySection: {
              ...s.paragraphsBySection,
              [activeSectionId]: res.paragraphs,
            },
            paragraphServerVersion: nextVersion,
            toastMessage: 'Section updated in another tab — refreshing.',
          };
        });
      } else {
        // Replace the cache regardless so reference-sign changes
        // propagate without the toast surface.
        set((s) => ({
          paragraphsBySection: {
            ...s.paragraphsBySection,
            [activeSectionId]: res.paragraphs,
          },
        }));
      }
    } catch {
      // Soft-fail: focus refresh never breaks the workbench.
    }
  },

  refreshSectionsForActiveMatter: async () => {
    // Closes the in-tab-nav gap: ``hydrate`` is gated on
    // ``hydratedMatterId !== matterId`` so a Spec → Claims → Spec
    // round-trip (same matter) didn't refetch anything. If a
    // background GE completed in that window, the section rail
    // showed pre-GE state ("10 empty") even though spec_paragraphs
    // had real content. The GE poll's eager refetch
    // (specStore.ts:2120) DOES update the store on terminal
    // status, but it depends on the poll being seeded — if the
    // job completed before the page mounted (e.g. drafter closed
    // the tab mid-GE and reopened), the eager refetch never
    // fires.
    //
    // Two parallel cheap reads — same shape the relevant slice of
    // ``hydrate`` uses, minus the 4 other endpoints that aren't
    // affected by background GE (feature mappings, examiner
    // flags, reference signs, drawing summaries). Soft-fail so
    // the workbench never breaks on a refresh hiccup.
    const { matterId, activeSectionId } = get();
    if (!matterId) return;
    try {
      const [stateRes, sectionsRes] = await Promise.all([
        getSpecState(matterId),
        getSpecSections(matterId),
      ]);
      if (stateRes.locked || _isLocked(sectionsRes)) return;

      // Cache invalidation pass — the previous version refreshed
      // the section rows but left ``paragraphsBySection`` intact,
      // so a Spec → Claims → Spec round-trip kept the stale
      // empty cache from before the background GE finished. Walk
      // the fresh sections list and drop the paragraph cache for
      // any section where the cached length disagrees with the
      // server's ``paragraph_count``. After this pass the next
      // ``loadActiveSectionParagraphs`` call for that section
      // refetches; Fix 1 ensures it won't short-circuit on an
      // empty cache.
      //
      // Spec-data-vanish guard — the
      // ``cachedCount > 0 && serverCount === 0`` branch used to
      // drop the cache here too. That branch caused the field-
      // reported "sections vanished after returning from a
      // stuck GE" symptom: ``paragraph_count`` is a live
      // ``COUNT(*)`` (backend/spec.py:951) so a transient zero
      // mid-write would wipe a fully-populated FE cache. The
      // drafter saw "empty" rail badges + the section body
      // also went blank because ``loadActiveSectionParagraphs``
      // honours an empty cache when it's been explicitly
      // dropped.
      // Removed the branch. Explicit drafter actions
      // (resetAllSections / deleteParagraph / resetSection)
      // mutate the cache directly, so the heuristic-based
      // drop on count=0 was redundant. The remaining branches
      // still catch the cross-tab / cross-process cases the
      // refresh was added for (cachedCount === 0 but
      // serverCount > 0 → drop empty cache so we refetch;
      // both counts > 0 but different → counts diverged,
      // drop cache).
      const currentParas = get().paragraphsBySection;
      const nextParas: typeof currentParas = { ...currentParas };
      let mutated = false;
      for (const section of sectionsRes) {
        const cached = currentParas[section.id];
        const cachedCount = Array.isArray(cached) ? cached.length : -1;
        const serverCount = section.paragraph_count ?? 0;
        if (cachedCount === -1) continue; // never cached — nothing to drop
        if (
          (cachedCount === 0 && serverCount > 0)
          || (cachedCount > 0 && serverCount > 0 && cachedCount !== serverCount)
        ) {
          delete nextParas[section.id];
          mutated = true;
        }
      }

      set({
        sections: sectionsRes,
        sectionSummary: stateRes.section_summary ?? null,
        ...(mutated ? { paragraphsBySection: nextParas } : {}),
      });

      // Force-fresh fetch for the active section so the content
      // area always reflects server truth on remount. Drops the
      // active section's cache entry (if any) and re-runs the
      // loader; loadActiveSectionParagraphs will see an empty
      // cache and hit the server.
      if (activeSectionId) {
        const afterInvalidate = get().paragraphsBySection;
        if (Object.prototype.hasOwnProperty.call(
          afterInvalidate, activeSectionId,
        )) {
          const cleared = { ...afterInvalidate };
          delete cleared[activeSectionId];
          set({ paragraphsBySection: cleared });
        }
        void get().loadActiveSectionParagraphs(activeSectionId);
      }
    } catch {
      // Soft-fail; the existing store data is preserved.
    }
  },

  loadSectionRender: async (sectionId, opts) => {
    const {
      matterId,
      renderEtagBySection,
      renderStatusBySection,
    } = get();
    if (!matterId) return;
    // Don't double-fetch while a request is already in flight.
    if (renderStatusBySection[sectionId] === 'loading') return;
    set((s) => ({
      renderStatusBySection: {
        ...s.renderStatusBySection,
        [sectionId]: 'loading',
      },
      renderErrorBySection: {
        ...s.renderErrorBySection,
        [sectionId]: null,
      },
    }));
    try {
      const ifNoneMatch = opts?.force
        ? undefined
        : renderEtagBySection[sectionId];
      const result = await getSpecSectionRender(matterId, sectionId, {
        ifNoneMatch,
      });
      if (result.kind === 'locked') {
        set({
          hydrationStatus: 'locked',
          lockedReason: result.data.locked_reason ?? 'drawings_not_frozen',
        });
        return;
      }
      if (result.kind === 'not_modified') {
        set((s) => ({
          renderStatusBySection: {
            ...s.renderStatusBySection,
            [sectionId]: 'idle',
          },
        }));
        return;
      }
      set((s) => ({
        renderBySection: {
          ...s.renderBySection,
          [sectionId]: result.data,
        },
        renderEtagBySection: {
          ...s.renderEtagBySection,
          [sectionId]: result.data.etag,
        },
        renderStatusBySection: {
          ...s.renderStatusBySection,
          [sectionId]: 'idle',
        },
      }));
    } catch (e: unknown) {
      const msg =
        (e as { message?: string })?.message ?? 'Render failed to load.';
      set((s) => ({
        renderStatusBySection: {
          ...s.renderStatusBySection,
          [sectionId]: 'error',
        },
        renderErrorBySection: {
          ...s.renderErrorBySection,
          [sectionId]: msg,
        },
      }));
    }
  },

  setSections: (sections) => set({ sections }),

  resetSection: async (sectionId: string) => {
    const { matterId } = get();
    if (!matterId) return { kind: 'error', message: 'No matter loaded.' };
    try {
      const result = await resetSpecSectionParagraphs(matterId, sectionId);
      // Optimistic update — zero the affected section's
      // paragraph_count + word_count and drop it from the
      // paragraph cache the moment the DELETE returns 200,
      // BEFORE the re-fetch runs. Closes the brief window
      // where the old (drafted) values would otherwise flash
      // in the header / rail. The Promise.all below replaces
      // these with the canonical server values; if it fails,
      // the optimistic zeros stay (still correct).
      set((s) => {
        const next = { ...s.paragraphsBySection };
        delete next[sectionId];
        const sections = s.sections.map((sec) =>
          sec.id === sectionId
            ? { ...sec, paragraph_count: 0, word_count: 0 }
            : sec,
        );
        return { paragraphsBySection: next, sections };
      });
      // Refresh sections + sectionSummary + specDocument in
      // parallel. The header subtitle reads sectionSummary.drafted
      // / .empty and specDocument.overall_completeness_pct;
      // skipping these would leave a stale "10 drafted" subtitle
      // on a freshly-reset matter (the bug followup commit on
      // commit 211f600 surfaced).
      try {
        const [fresh, stateRes] = await Promise.all([
          getSpecSections(matterId),
          getSpecState(matterId),
        ]);
        set((s) => ({
          sections: Array.isArray(fresh) ? fresh : s.sections,
          sectionSummary: stateRes.locked
            ? s.sectionSummary
            : stateRes.section_summary ?? s.sectionSummary,
          specDocument: stateRes.locked
            ? s.specDocument
            : stateRes.spec_document ?? s.specDocument,
        }));
      } catch {
        /* refresh best-effort; next focus tick repairs */
      }
      return { kind: 'ok', deleted: result.paragraphs_deleted };
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: { message?: string } | string } } })
        ?.response?.data?.detail;
      const text = typeof msg === 'string'
        ? msg
        : (msg?.message || 'Reset failed.');
      return { kind: 'error', message: text };
    }
  },

  resetAllSections: async () => {
    const { matterId } = get();
    if (!matterId) return { kind: 'error', message: 'No matter loaded.' };
    try {
      const result = await resetAllSpecParagraphs(matterId);
      // Optimistic update — zero every non-auto, non-frozen
      // section's paragraph_count + word_count and the
      // document-level overall_completeness_pct + the section
      // summary's drafted/empty counters the moment the bulk
      // DELETE returns 200, BEFORE the re-fetch runs. Closes
      // the brief flash where the header subtitle showed
      // "10 drafted · 100% complete" even though every section
      // was just emptied. The Promise.all below replaces these
      // with the canonical server values; if the re-fetch
      // fails, the optimistic zeros stay (still correct —
      // backend already wrote them in the same transaction at
      // spec.py:2579 + _reset_section_paragraphs).
      set((s) => {
        const sections = s.sections.map((sec) => (
          sec.is_auto_generated || sec.is_frozen
            ? sec
            : { ...sec, paragraph_count: 0, word_count: 0 }
        ));
        const nonAutoNonFrozen = sections.filter(
          (sec) => !sec.is_auto_generated && !sec.is_frozen,
        );
        const sectionSummary = s.sectionSummary
          ? {
              ...s.sectionSummary,
              drafted: 0,
              empty: nonAutoNonFrozen.length,
            }
          : s.sectionSummary;
        const specDocument = s.specDocument
          ? { ...s.specDocument, overall_completeness_pct: 0 }
          : s.specDocument;
        return {
          paragraphsBySection: {},
          sections,
          sectionSummary,
          specDocument,
        };
      });
      try {
        const [fresh, stateRes] = await Promise.all([
          getSpecSections(matterId),
          getSpecState(matterId),
        ]);
        set((s) => ({
          sections: Array.isArray(fresh) ? fresh : s.sections,
          sectionSummary: stateRes.locked
            ? s.sectionSummary
            : stateRes.section_summary ?? s.sectionSummary,
          specDocument: stateRes.locked
            ? s.specDocument
            : stateRes.spec_document ?? s.specDocument,
        }));
      } catch {
        /* best-effort */
      }
      return {
        kind: 'ok',
        sections: result.sections_reset,
        deleted: result.paragraphs_deleted,
      };
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: { message?: string } | string } } })
        ?.response?.data?.detail;
      const text = typeof msg === 'string'
        ? msg
        : (msg?.message || 'Reset failed.');
      return { kind: 'error', message: text };
    }
  },

  setActiveSection: (sectionId) => {
    const { matterId, sections } = get();
    set({ activeSectionId: sectionId, activeParagraphId: null });
    if (matterId) _writeActiveSection(matterId, sectionId);
    if (sectionId) {
      const section = sections.find((s) => s.id === sectionId);
      if (section?.is_auto_generated) {
        // Auto sections render server-side; lazy-fetch the payload.
        void get().loadSectionRender(sectionId);
      } else {
        // Lazy-load the paragraphs for the newly active section.
        void get().loadActiveSectionParagraphs(sectionId);
      }
    }
  },

  setActiveParagraph: (paragraphId) => set({ activeParagraphId: paragraphId }),

  toggleLeftRail: () =>
    set((s) => {
      const next = !s.leftCollapsed;
      _writeBool(STORAGE_KEY_LEFT_COLLAPSED, next);
      return { leftCollapsed: next };
    }),

  toggleRightRail: () =>
    set((s) => {
      const next = !s.rightCollapsed;
      _writeBool(STORAGE_KEY_RIGHT_COLLAPSED, next);
      return { rightCollapsed: next };
    }),

  toggleDock: (key) =>
    set((s) => {
      const next = { ...s.docksOpen, [key]: !s.docksOpen[key] };
      _writeDocksOpen(next);
      return { docksOpen: next };
    }),

  setDockOpen: (key, open) =>
    set((s) => {
      if (s.docksOpen[key] === open) return s;
      const next = { ...s.docksOpen, [key]: open };
      _writeDocksOpen(next);
      return { docksOpen: next };
    }),

  showToast: (message) => {
    set({ toastMessage: message });
    if (message && typeof window !== 'undefined') {
      window.setTimeout(() => {
        const cur = useSpecStore.getState().toastMessage;
        if (cur === message) set({ toastMessage: null });
      }, 5000);
    }
  },

  // Reset on matter change so stale state from matter A doesn't
  // briefly flash when matter B opens.
  resetForMatter: (matterId) =>
    set({
      hydrationStatus: 'idle',
      lockedReason: null,
      errorMessage: null,
      hydratedMatterId: null,
      matterId,
      specDocument: null,
      sectionSummary: null,
      sections: [],
      paragraphsBySection: {},
      paragraphServerVersion: {},
      examinerFlags: [],
      featureMappings: [],
      revisionProposals: [],
      glossaryEntries: [],
      glossaryStatus: 'idle',
      termWeb: [],
      termWebStatus: 'idle',
      idfCoverage: null,
      idfCoverageStatus: 'idle',
      proposalSubmitStatus: 'idle',
      popoutFigureId: null,
      generateEverythingJobId: null,
      generateEverythingStatus: null,
      generateEverythingFailures: [],
      generateEverythingCapBlocked: null,
      generateEverythingRuns: [],
      referenceSigns: [],
      drawingSummaries: [],
      examinerStatus: 'idle',
      examinerError: null,
      examinerSummary: null,
      sectionVerifyIssues: {},
      sectionVerifyStatus: {},
      sectionVerifyScore: {},
      lastAnchoredParagraphId: null,
      renderBySection: {},
      renderEtagBySection: {},
      renderStatusBySection: {},
      renderErrorBySection: {},
      activeSectionId: null,
      activeParagraphId: null,
      generateStatus: 'idle',
      generateError: null,
      pendingOverwrite: null,
      paragraphSaveStatus: 'idle',
      paragraphSaveError: null,
      lastSavedAt: null,
      pendingSaveCount: 0,
      paragraphMutationStatus: 'idle',
      paragraphMutationError: null,
      paragraphMutationTargetId: null,
      nlEditStatus: 'idle',
      nlEditError: null,
      lastNlEditSummary: null,
      lastNlEditWarnings: [],
      offlineMode: false,
      pendingOfflineCount: 0,
      anchorWarnings: {},
      numeralWarnings: {},
      figureWarnings: {},
      findingWarnings: {},
      pendingDiffBySection: {},
    }),

  // ── Section generate (Slice 5D) ──────────────────────────────
  generateSection: async ({
    sectionId, drafterIntent = '', overwrite = false, mode = 'rewrite',
  }) => {
    const { matterId, paragraphsBySection } = get();
    if (!matterId) {
      return {
        kind: 'error',
        message: 'No active matter; cannot generate.',
      };
    }
    // Snapshot the pre-regenerate paragraphs BEFORE the POST so
    // the diff/revert panel can replay them. Only meaningful when
    // we're overwriting non-empty content — a first-time generate
    // has nothing to revert to. Snapshot is held in a local for
    // now; we only commit it to pendingDiffBySection on a
    // successful overwrite (covered below).
    const cachedPrev = paragraphsBySection[sectionId] || [];
    const overwriteSnapshot: SpecParagraph[] | null =
      overwrite && mode !== 'cover_missing' && cachedPrev.length > 0
        ? cachedPrev.map((p) => ({ ...p }))
        : null;
    set({
      generateStatus: 'generating',
      generateError: null,
    });
    try {
      const res = await postSpecSectionGenerate(matterId, sectionId, {
        drafter_intent: drafterIntent,
        overwrite,
        // Only sent when it is not the default, so an ordinary
        // generate puts exactly the same bytes on the wire as
        // before this field existed.
        ...(mode !== 'rewrite' ? { mode } : {}),
      });

      // Locked-state envelope: race between hydration and click.
      if ((res as SpecStateResponse).locked) {
        const reason = (res as SpecStateResponse).locked_reason || 'drawings_not_frozen';
        set({
          generateStatus: 'idle',
          generateError: null,
          pendingOverwrite: null,
        });
        return { kind: 'locked', reason };
      }

      // Happy path: update store + clear pendingOverwrite.
      const ok = res as SpecSectionGenerateResponse;
      set((s) => {
        // Anchor-check warning: only meaningful when the backend
        // ran the check AND surfaced missing features. Clear the
        // entry on every successful regenerate so a stale list
        // can't linger after the drafter fixes coverage.
        const ac = ok.generation_meta?.anchor_check;
        const nextAnchor = { ...s.anchorWarnings };
        if (ac?.ran && ac.missing_features.length > 0) {
          nextAnchor[sectionId] = {
            missing:   ac.missing_features,
            dependent: !!ac.dependent_features_checked,
          };
        } else {
          delete nextAnchor[sectionId];
        }
        // Invented reference signs. Same lifecycle as the anchor
        // entry above: cleared on every successful regenerate so a
        // fixed numeral doesn't keep warning. `ran: false` means
        // the matter has no registry — nothing to say, not "clean".
        const un = ok.generation_meta?.unregistered_numerals;
        const nextNumeral = { ...s.numeralWarnings };
        if (un?.ran && un.numerals.length > 0) {
          nextNumeral[sectionId] = {
            numerals: un.numerals,
            where: un.where || {},
          };
        } else {
          delete nextNumeral[sectionId];
        }
        // Text cut for citing a figure that is not frozen (NB-11).
        const rf = ok.generation_meta?.removed_figures;
        const nextFigure = { ...s.figureWarnings };
        if (rf && rf.figures.length > 0) {
          nextFigure[sectionId] = { figures: rf.figures, excerpts: rf.excerpts || [] };
        } else {
          delete nextFigure[sectionId];
        }
        // NB-17 / NB-28 / NB-29 — what to check in the new text.
        const nextFinding = { ...s.findingWarnings };
        const findings = pickFindings(ok.generation_meta);
        if (findings) {
          nextFinding[sectionId] = findings;
        } else {
          delete nextFinding[sectionId];
        }
        // Populate (or clear) the post-regenerate diff. Only
        // captured when we have a pre-state to diff against —
        // empty-section first-time generates have nothing to
        // revert to, so the panel stays dormant.
        const nextDiff = { ...s.pendingDiffBySection };
        if (overwriteSnapshot && overwriteSnapshot.length > 0) {
          nextDiff[sectionId] = {
            previous: overwriteSnapshot,
            steering: drafterIntent.trim() ? drafterIntent.trim() : null,
            timestamp: Date.now(),
          };
        } else {
          // Defensive: a fresh generate on a previously-diffed
          // section supersedes the old snapshot — clear it so the
          // panel doesn't show a stale comparison.
          delete nextDiff[sectionId];
        }
        return {
          generateStatus: 'idle',
          generateError: null,
          pendingOverwrite: null,
          // Replace this section's paragraph cache.
          paragraphsBySection: {
            ...s.paragraphsBySection,
            [sectionId]: ok.paragraphs as SpecParagraph[],
          },
          pendingDiffBySection: nextDiff,
          // Refresh the section row in-place so UI badges reflect
          // the new state without an extra /sections fetch.
          sections: s.sections.map((sec) =>
            sec.id === sectionId && ok.section
              ? {
                  ...sec,
                  ...ok.section,
                  paragraph_count: ok.paragraphs.length,
                  ui_state: ok.paragraphs.length > 0 ? 'active' : 'empty',
                  meta_text:
                    ok.paragraphs.length > 0
                      ? `${ok.paragraphs.reduce((n, p) => n + (p.word_count || 0), 0)} w`
                      : 'empty',
                }
              : sec,
          ),
          activeSectionId: sectionId,
          anchorWarnings: nextAnchor,
          numeralWarnings: nextNumeral,
          figureWarnings: nextFigure,
          findingWarnings: nextFinding,
        };
      });
      // FIX 3 — coverage live-refresh. The audit's headline
      // numbers (covered/missing, percentage) become stale the
      // moment a section regenerates. We trigger a server-side
      // re-run with a small delay so the new paragraph rows
      // have settled in the DB before the audit's word-boundary
      // scan reads them. Gated on ``idfCoverage`` already being
      // present so drafters who haven't engaged with coverage
      // don't get a surprise network call.
      if (get().idfCoverage) {
        setTimeout(() => {
          void get().loadIdfCoverage(true);
        }, 2000);
      }
      // BUG-S5-008 — refresh sectionSummary + specDocument so
      // the CompletenessBar (driven by
      // ``sectionSummary.drafted / (total - 2)``) and the
      // header subtitle reflect the new drafted count. The
      // in-place ``sections.map`` above updates THIS section's
      // paragraph_count but doesn't recompute the rollup. Fire-
      // and-forget: a stale rollup self-heals on the next
      // mount or background-job tick, so we don't gate the
      // return on it.
      void (async () => {
        try {
          const stateRes = await getSpecState(matterId);
          if (stateRes.locked) return;
          useSpecStore.setState((s) => ({
            sectionSummary: stateRes.section_summary ?? s.sectionSummary,
            specDocument: stateRes.spec_document ?? s.specDocument,
          }));
        } catch {
          /* best-effort refresh */
        }
      })();
      return { kind: 'ok', response: ok };
    } catch (e: unknown) {
      const err = e as {
        response?: {
          status?: number;
          data?: {
            detail?: {
              error_code?: string;
              paragraph_count?: number;
              message?: string;
            };
          };
        };
        message?: string;
      };
      const status = err?.response?.status ?? 0;
      const detail = err?.response?.data?.detail ?? {};
      const errorCode = detail.error_code;

      // 409 + section_not_empty → cooperative confirm branch.
      if (status === 409 && errorCode === 'section_not_empty') {
        const paragraphCount = detail.paragraph_count ?? 0;
        set({
          generateStatus: 'idle',
          generateError: null,
          pendingOverwrite: {
            sectionId,
            paragraphCount,
            drafterIntent,
          },
        });
        return {
          kind: 'confirm_overwrite',
          sectionId,
          paragraphCount,
        };
      }

      const message =
        detail.message ?? err?.message ?? 'Section generate failed.';
      set({
        generateStatus: 'error',
        generateError: message,
        pendingOverwrite: null,
      });
      return { kind: 'error', message, errorCode };
    }
  },

  confirmGenerateOverwrite: async () => {
    const { pendingOverwrite, generateSection } = get();
    if (!pendingOverwrite) {
      return { kind: 'error', message: 'No pending overwrite.' };
    }
    return generateSection({
      sectionId: pendingOverwrite.sectionId,
      drafterIntent: pendingOverwrite.drafterIntent,
      overwrite: true,
    });
  },

  cancelGenerateOverwrite: () =>
    set({ pendingOverwrite: null, generateStatus: 'idle' }),

  setPendingRegenerateWithSteering: (pending) =>
    set({ pendingRegenerateWithSteering: pending }),

  // ── Paragraph auto-save (Slice 5E) ────────────────────────────
  updateParagraphLocalAndQueueSave: ({
    paragraphId, sectionId, bodyHtml, bodyText,
  }) => {
    // 1. Optimistic local update — UI surfaces edits without a
    //    server round-trip. body_html is the source of truth for
    //    contentEditable; body_text is the plain mirror used by
    //    word_count + indexing.
    set((s) => {
      const existing = s.paragraphsBySection[sectionId] ?? [];
      const next = existing.map((p) =>
        p.id === paragraphId
          ? {
              ...p,
              body_html: bodyHtml,
              body_text: bodyText,
              word_count: bodyText.split(/\s+/).filter(Boolean).length,
            }
          : p,
      );
      return {
        paragraphsBySection: { ...s.paragraphsBySection, [sectionId]: next },
      };
    });

    // 2. Debounce the PATCH per-paragraph. A new keystroke clears
    //    the prior timer; the PATCH only fires after
    //    PARAGRAPH_DEBOUNCE_MS of idle time on this paragraph.
    if (_paragraphSaveTimers[paragraphId]) {
      clearTimeout(_paragraphSaveTimers[paragraphId]);
    }
    _paragraphSaveTimers[paragraphId] = setTimeout(() => {
      delete _paragraphSaveTimers[paragraphId];
      void _persistParagraphNow(paragraphId, sectionId, bodyHtml, bodyText);
    }, PARAGRAPH_DEBOUNCE_MS);
  },

  flushParagraphSaves: async () => {
    // Clear all debounce timers and fire pending saves. Used by
    // SpecPage's unmount flush so a "back to matter" doesn't drop
    // an in-flight edit. Reads each paragraph's current body from
    // the store (not from the timer closure) so the most recent
    // local edits win.
    const ids = Object.keys(_paragraphSaveTimers);
    for (const pid of ids) {
      clearTimeout(_paragraphSaveTimers[pid]);
      delete _paragraphSaveTimers[pid];
    }
    const { paragraphsBySection } = get();
    const tasks: Promise<void>[] = [];
    for (const sectionId of Object.keys(paragraphsBySection)) {
      const paragraphs: SpecParagraph[] = paragraphsBySection[sectionId] ?? [];
      for (const p of paragraphs) {
        if (ids.includes(p.id)) {
          tasks.push(
            _persistParagraphNow(p.id, sectionId, p.body_html, p.body_text),
          );
        }
      }
    }
    await Promise.all(tasks);
  },

  // ── Paragraph expand (Slice 6D) ──────────────────────────────
  expandParagraph: async ({ paragraphId, drafterIntent = '' }) => {
    const sectionId = _findSectionIdForParagraph(get(), paragraphId);
    if (!sectionId) {
      return { kind: 'error', message: 'Paragraph not in active section.' };
    }
    return _runParagraphMutation(
      'expanding', paragraphId, sectionId,
      (matterId) =>
        postSpecParagraphExpand(matterId, sectionId, paragraphId, {
          drafter_intent: drafterIntent,
        }),
      (resp) => (resp as SpecParagraphExpandResponse).expansion_meta.char_diff,
    );
  },

  // ── Paragraph tighten (Slice 6D) ─────────────────────────────
  tightenParagraph: async ({ paragraphId, drafterIntent = '' }) => {
    const sectionId = _findSectionIdForParagraph(get(), paragraphId);
    if (!sectionId) {
      return { kind: 'error', message: 'Paragraph not in active section.' };
    }
    return _runParagraphMutation(
      'tightening', paragraphId, sectionId,
      (matterId) =>
        postSpecParagraphTighten(matterId, sectionId, paragraphId, {
          drafter_intent: drafterIntent,
        }),
      (resp) =>
        (resp as SpecParagraphTightenResponse).tightening_meta.char_diff,
    );
  },

  // ── NL edit (Slice 6D) ───────────────────────────────────────
  applyNlEdit: async ({ sectionId, drafterRequest }) => {
    const { matterId } = get();
    if (!matterId) {
      return { kind: 'error', message: 'No active matter.' };
    }
    if (!drafterRequest.trim()) {
      return {
        kind: 'error',
        message: 'NL-edit request cannot be empty.',
        errorCode: 'drafter_request_required',
      };
    }
    set({ nlEditStatus: 'pending', nlEditError: null });
    try {
      const res = await postSpecSectionNlEdit(matterId, sectionId, {
        drafter_request: drafterRequest,
      });
      if ((res as SpecStateResponse).locked) {
        const reason = (res as SpecStateResponse).locked_reason || 'drawings_not_frozen';
        set({ nlEditStatus: 'idle', nlEditError: null });
        return { kind: 'locked', reason };
      }
      const ok = res as SpecNlEditResponse;
      // Patch each affected paragraph in the local cache so the
      // editor reflects the new text without a refetch.
      set((s) => {
        const next = { ...s.paragraphsBySection };
        const list = (next[sectionId] ?? []).map((p) => {
          const updated = ok.affected_paragraphs.find((ap) => ap.id === p.id);
          return updated ? { ...p, ...updated } : p;
        });
        next[sectionId] = list;
        // BUG-015 — bump the server-version counter for each
        // affected paragraph so ParagraphBlock re-reconciles.
        const nextVersion = { ...s.paragraphServerVersion };
        for (const ap of ok.affected_paragraphs) {
          nextVersion[ap.id] = (nextVersion[ap.id] ?? 0) + 1;
        }
        return {
          paragraphsBySection: next,
          paragraphServerVersion: nextVersion,
          nlEditStatus: 'idle',
          nlEditError: null,
          lastNlEditSummary: ok.nl_edit_meta.changes_summary || null,
          lastNlEditWarnings: ok.nl_edit_meta.warnings,
          lastSavedAt: new Date().toISOString(),
        };
      });
      return { kind: 'ok', response: ok };
    } catch (e: unknown) {
      const err = e as {
        response?: { data?: { detail?: { error_code?: string; message?: string } } };
        message?: string;
      };
      const detail = err?.response?.data?.detail ?? {};
      const message =
        detail.message ?? err?.message ?? 'NL-edit failed.';
      set({
        nlEditStatus: 'error',
        nlEditError: message,
      });
      return { kind: 'error', message, errorCode: detail.error_code };
    }
  },

  clearNlEditWarnings: () =>
    set({ lastNlEditWarnings: [], lastNlEditSummary: null }),

  // ── Hover-rail actions (Slice 6E + Phase 12F mid-section insert) ─
  addParagraphToSection: async (sectionId: string, opts) => {
    const { matterId } = get();
    if (!matterId) return null;
    try {
      const res = await postSpecParagraphCreate(matterId, sectionId, {
        body_html: '<p></p>',
        body_text: '',
        // Phase 12F — mid-section insert. The hover-rail Add button
        // on a paragraph passes its own id here; clicking Add at the
        // section level (no source paragraph) appends as before.
        ...(opts?.afterParagraphId
          ? { after_paragraph_id: opts.afterParagraphId }
          : {}),
      });
      if ((res as SpecStateResponse).locked) {
        useSpecStore.getState().showToast(
          'Specification locked: drawings are no longer frozen.',
        );
        return null;
      }
      const ok = res as { paragraph: SpecParagraph };
      // Insert into the local cache at the right spot. For an
      // append (no after_paragraph_id), goes at the end. For a
      // mid-section insert, slots in directly after the source
      // paragraph by sequence_num — local cache mirrors the
      // server-side shift so subsequent paragraphs render in the
      // right order without an extra fetch.
      set((s) => {
        const next = { ...s.paragraphsBySection };
        const list = next[sectionId] ?? [];
        if (opts?.afterParagraphId) {
          const idx = list.findIndex((p) => p.id === opts.afterParagraphId);
          // Bump every list entry after the insertion point so
          // their sequence_num stays in sync with the server's
          // shift. The new paragraph slots in at idx + 1.
          const shifted = list.map((p, i) =>
            i > idx ? { ...p, sequence_num: p.sequence_num + 1 } : p,
          );
          next[sectionId] = [
            ...shifted.slice(0, idx + 1),
            ok.paragraph,
            ...shifted.slice(idx + 1),
          ];
        } else {
          next[sectionId] = [...list, ok.paragraph];
        }
        return {
          paragraphsBySection: next,
          activeParagraphId: ok.paragraph.id,
          lastSavedAt: new Date().toISOString(),
        };
      });
      return ok.paragraph;
    } catch (e: unknown) {
      const err = e as {
        response?: { data?: { detail?: { message?: string } } };
        message?: string;
      };
      const msg =
        err?.response?.data?.detail?.message ??
        err?.message ?? 'Add paragraph failed.';
      useSpecStore.getState().showToast(`Add failed: ${msg}`);
      return null;
    }
  },

  hydrateOfflineQueue: async () => {
    const queue = await loadOfflineQueue();
    const count = Object.keys(queue).length;
    useSpecStore.setState({
      pendingOfflineCount: count,
      offlineMode: count > 0,
    });
    if (count > 0) {
      // Best-effort drain on hydrate. Failure leaves the queue intact.
      void useSpecStore.getState().flushOfflineQueue();
    }
  },

  flushOfflineQueue: async () => {
    const { matterId } = get();
    if (!matterId) return 0;
    const queue = await loadOfflineQueue();
    const entries = Object.values(queue).filter(
      (e: SpecOfflineQueueEntry) => e.matterId === matterId,
    );
    if (entries.length === 0) {
      set({ offlineMode: false, pendingOfflineCount: 0 });
      return 0;
    }
    const drained: string[] = [];
    for (const entry of entries) {
      try {
        await patchSpecParagraph(
          entry.matterId, entry.sectionId, entry.paragraphId,
          { body_html: entry.bodyHtml, body_text: entry.bodyText },
        );
        drained.push(entry.paragraphId);
      } catch {
        // Still offline / still failing. Stop here and leave the
        // rest in the queue for the next attempt.
        break;
      }
    }
    if (drained.length > 0) {
      const remaining = await removeOfflineEntries(drained);
      const remainingCount = Object.keys(remaining).length;
      set((s) => ({
        pendingOfflineCount: remainingCount,
        offlineMode: remainingCount > 0,
        lastSavedAt: drained.length > 0
          ? new Date().toISOString()
          : s.lastSavedAt,
      }));
    }
    return drained.length;
  },

  toggleParagraphLock: async (sectionId, paragraphId) => {
    const { matterId, paragraphsBySection } = get();
    if (!matterId) {
      return { kind: 'error', message: 'No active matter.' };
    }
    const cached = paragraphsBySection[sectionId] || [];
    const current = cached.find((p) => p.id === paragraphId);
    const nextLocked = !current?.is_locked;
    try {
      const res = await patchSpecParagraphLock(
        matterId, sectionId, paragraphId, nextLocked,
      );
      if ((res as SpecStateResponse).locked) {
        return {
          kind: 'locked',
          reason: (res as SpecStateResponse).locked_reason
            || 'spec is locked',
        };
      }
      const ok = res as { paragraph: SpecParagraph };
      set((s) => {
        const list = s.paragraphsBySection[sectionId] || [];
        return {
          paragraphsBySection: {
            ...s.paragraphsBySection,
            [sectionId]: list.map((p) =>
              p.id === paragraphId ? { ...p, is_locked: ok.paragraph.is_locked } : p,
            ),
          },
        };
      });
      return { kind: 'ok', isLocked: !!ok.paragraph.is_locked };
    } catch (e: unknown) {
      const err = e as {
        response?: { data?: { detail?: { message?: string } } };
        message?: string;
      };
      const msg =
        err?.response?.data?.detail?.message
        ?? err?.message ?? 'Lock toggle failed.';
      return { kind: 'error', message: msg };
    }
  },

  deleteParagraph: async (paragraphId: string) => {
    const { matterId } = get();
    if (!matterId) {
      return { kind: 'error', message: 'No active matter.' };
    }
    const sectionId = _findSectionIdForParagraph(get(), paragraphId);
    if (!sectionId) {
      return {
        kind: 'error',
        message: 'Paragraph not in active section cache.',
      };
    }
    try {
      await deleteSpecParagraph(matterId, sectionId, paragraphId);
      // BUG-061 — backend now resequences every surviving paragraph
      // in the matter to collapse the [NNNN] gap. The local cache
      // would otherwise keep the old numbers (and downstream
      // sections would silently disagree with the server). Clear
      // paragraphsBySection so PaperEditor's prefetch effect
      // re-fetches each non-auto section with its fresh numbering.
      set((s) => ({
        paragraphsBySection: {},
        activeParagraphId:
          s.activeParagraphId === paragraphId ? null : s.activeParagraphId,
        lastSavedAt: new Date().toISOString(),
      }));
      return { kind: 'ok' };
    } catch (e: unknown) {
      const err = e as {
        response?: { data?: { detail?: { message?: string } } };
        message?: string;
      };
      const msg =
        err?.response?.data?.detail?.message ??
        err?.message ?? 'Delete failed.';
      useSpecStore.getState().showToast(`Delete failed: ${msg}`);
      return { kind: 'error', message: msg };
    }
  },

  // ── Examiner / verify (Slice 8C) ─────────────────────────────
  runExaminerReview: async (opts) => {
    const { matterId } = get();
    if (!matterId) return { kind: 'error', message: 'No matter loaded.' };
    // BUG-014 — guard against the "button silently disabled
    // forever" failure mode. If a prior call stranded
    // examinerStatus at 'running' (e.g. unexpected response
    // shape threw between the await and the final set), the
    // button would be permanently disabled with no path to
    // recovery short of a full page reload. Wrapping the body
    // in try / finally ensures examinerStatus always lands at
    // a terminal state regardless of which throw path fires.
    set({ examinerStatus: 'running', examinerError: null });
    try {
      const result = await postSpecExaminerReview(matterId, {
        force: opts?.force ?? false,
      });
      if (result.kind === 'locked') {
        set({
          examinerStatus: 'idle',
          hydrationStatus: 'locked',
          lockedReason: result.data.locked_reason ?? 'drawings_not_frozen',
        });
        return { kind: 'locked' };
      }
      if (result.kind === 'error') {
        set({
          examinerStatus: 'error',
          examinerError: result.message,
        });
        return { kind: 'error', message: result.message };
      }
      const flags = Array.isArray(result.data.flags) ? result.data.flags : [];
      set((s) => {
        if (opts?.reveal === false) {
          return {
            examinerStatus: 'idle',
            examinerSummary: result.data.summary,
            examinerFlags: flags,
          };
        }
        // Show the results: open the right rail and the Examiner dock.
        if (s.rightCollapsed) _writeBool(STORAGE_KEY_RIGHT_COLLAPSED, false);
        const docksOpen = s.docksOpen.examinerView
          ? s.docksOpen : { ...s.docksOpen, examinerView: true };
        if (docksOpen !== s.docksOpen) _writeDocksOpen(docksOpen);
        return {
          examinerStatus: 'idle',
          examinerSummary: result.data.summary,
          examinerFlags: flags,
          rightCollapsed: false,
          docksOpen,
          examinerRevealNonce: s.examinerRevealNonce + 1,
        };
      });
      const openCount = flags.filter(
        (f) => f.status === 'open' || f.status === 'acknowledged',
      ).length;
      return { kind: result.data.cached ? 'cached' : 'ok', openCount };
    } catch (e: unknown) {
      // Any unexpected throw (malformed response, store-shape
      // mismatch, etc.) — surface as error rather than leaving
      // examinerStatus stranded at 'running'.
      const msg = (e as { message?: string })?.message
        ?? 'Examiner review failed unexpectedly.';
      set({ examinerStatus: 'error', examinerError: msg });
      return { kind: 'error', message: msg };
    }
  },

  verifySection: async (sectionId) => {
    const { matterId } = get();
    if (!matterId) return { kind: 'error', message: 'No matter loaded.' };
    // ARCH-F02 (Phase 25 3-B) — wrap the postSpecSectionVerify
    // call in try/catch so a network failure / HTTP 500 / any
    // unexpected throw lands the section at status='error'
    // instead of stranding it at 'running' forever (which made
    // the Verify button look stuck in a perpetual spinner with
    // no recovery short of a full page reload). Mirrors
    // runExaminerReview above.
    set((s) => ({
      sectionVerifyStatus: {
        ...s.sectionVerifyStatus,
        [sectionId]: 'running',
      },
    }));
    try {
      const result = await postSpecSectionVerify(matterId, sectionId);
      if (result.kind === 'locked') {
        set((s) => ({
          sectionVerifyStatus: {
            ...s.sectionVerifyStatus,
            [sectionId]: 'idle',
          },
          hydrationStatus: 'locked',
          lockedReason: result.data.locked_reason ?? 'drawings_not_frozen',
        }));
        return { kind: 'locked' };
      }
      if (result.kind === 'error') {
        set((s) => ({
          sectionVerifyStatus: {
            ...s.sectionVerifyStatus,
            [sectionId]: 'error',
          },
        }));
        return { kind: 'error', message: result.message };
      }
      set((s) => ({
        sectionVerifyStatus: {
          ...s.sectionVerifyStatus,
          [sectionId]: 'idle',
        },
        sectionVerifyIssues: {
          ...s.sectionVerifyIssues,
          [sectionId]: result.data.issues,
        },
        sectionVerifyScore: {
          ...s.sectionVerifyScore,
          [sectionId]: result.data.score,
        },
      }));
      return { kind: 'ok' };
    } catch (e: unknown) {
      // ARCH-F02 (Phase 25 3-B) — any unexpected throw (network
      // failure, 500 surfaced as exception, malformed response
      // shape that survived the client's kind discriminator) now
      // lands at 'error' so the section's Verify button shows an
      // error indicator the user can click to retry, rather than
      // a dead spinner.
      // eslint-disable-next-line no-console -- intentional diagnostic
      console.error(
        '[specStore] verifySection failed for section', sectionId, e,
      );
      set((s) => ({
        sectionVerifyStatus: {
          ...s.sectionVerifyStatus,
          [sectionId]: 'error',
        },
      }));
      const msg = (e as { message?: string })?.message
        ?? 'Verify failed unexpectedly.';
      return { kind: 'error', message: msg };
    }
  },

  resolveFlag: async (flagId, resolutionNote) => {
    const { matterId } = get();
    if (!matterId) return;
    try {
      const { flag } = await import('@/api/client').then((m) =>
        m.postSpecFlagResolve(matterId, flagId, resolutionNote),
      );
      set((s) => ({
        examinerFlags: s.examinerFlags.map(
          (f) => (f.id === flag.id ? flag : f),
        ),
      }));
    } catch (e: unknown) {
      const msg = (e as { message?: string })?.message ?? 'resolve failed';
      get().showToast(`Resolve failed: ${msg}`);
    }
  },

  dismissFlag: async (flagId, dismissNote) => {
    const { matterId } = get();
    if (!matterId) return;
    try {
      const { flag } = await import('@/api/client').then((m) =>
        m.postSpecFlagDismiss(matterId, flagId, dismissNote),
      );
      set((s) => ({
        examinerFlags: s.examinerFlags.map(
          (f) => (f.id === flag.id ? flag : f),
        ),
      }));
    } catch (e: unknown) {
      const msg = (e as { message?: string })?.message ?? 'dismiss failed';
      get().showToast(`Dismiss failed: ${msg}`);
    }
  },

  acknowledgeFlag: async (flagId) => {
    const { matterId } = get();
    if (!matterId) return;
    try {
      const { flag } = await import('@/api/client').then((m) =>
        m.postSpecFlagAcknowledge(matterId, flagId),
      );
      set((s) => ({
        examinerFlags: s.examinerFlags.map(
          (f) => (f.id === flag.id ? flag : f),
        ),
      }));
    } catch (e: unknown) {
      const msg = (e as { message?: string })?.message ?? 'ack failed';
      get().showToast(`Acknowledge failed: ${msg}`);
    }
  },

  // ── Feature drag-anchor (Slice 8D) ───────────────────────────
  // ── Revision proposals + glossary (Slice 9B) ─────────────────
  submitRevisionProposal: async (body) => {
    const { matterId } = get();
    if (!matterId) {
      return { kind: 'error', message: 'No matter loaded.' };
    }
    set({ proposalSubmitStatus: 'submitting' });
    const result = await postSpecRevisionProposal(matterId, body);
    if (result.kind === 'locked') {
      set({
        proposalSubmitStatus: 'idle',
        hydrationStatus: 'locked',
        lockedReason: result.data.locked_reason ?? 'drawings_not_frozen',
      });
      return { kind: 'locked',
               reason: result.data.locked_reason ?? 'drawings_not_frozen' };
    }
    if (result.kind === 'error') {
      set({ proposalSubmitStatus: 'error' });
      return { kind: 'error', message: result.message,
               error_code: result.error_code };
    }
    // Insert proposal into store + reset status.
    const { proposal, redirect_url: redirectUrl } = result.data;
    set((s) => ({
      proposalSubmitStatus: 'idle',
      revisionProposals: [proposal, ...s.revisionProposals],
    }));
    return { kind: 'ok', proposal, redirectUrl };
  },

  cancelRevisionProposal: async (proposalId) => {
    const { matterId } = get();
    if (!matterId) return;
    try {
      const { proposal } = await patchSpecRevisionProposal(
        matterId, proposalId, { status: 'cancelled' },
      );
      set((s) => ({
        revisionProposals: s.revisionProposals.map(
          (p) => (p.id === proposal.id ? proposal : p),
        ),
      }));
    } catch (e: unknown) {
      const msg = (e as { message?: string })?.message ?? 'cancel failed';
      get().showToast(`Cancel proposal failed: ${msg}`);
    }
  },

  markRevisionProposalConsumed: async (proposalId, resolutionNote) => {
    const { matterId } = get();
    if (!matterId) return;
    try {
      const { proposal } = await postSpecRevisionProposalMarkConsumed(
        matterId, proposalId, resolutionNote,
      );
      set((s) => ({
        revisionProposals: s.revisionProposals.map(
          (p) => (p.id === proposal.id ? proposal : p),
        ),
      }));
    } catch (e: unknown) {
      const msg = (e as { message?: string })?.message ?? 'consume failed';
      get().showToast(`Mark consumed failed: ${msg}`);
    }
  },

  loadGlossary: async () => {
    const { matterId, glossaryStatus, glossaryEntries } = get();
    if (!matterId) return;
    if (glossaryStatus === 'loading') return;
    if (glossaryEntries.length > 0) return; // cache hit
    set({ glossaryStatus: 'loading' });
    try {
      const entries = await getSpecGlossary(matterId);
      set({ glossaryEntries: entries, glossaryStatus: 'idle' });
    } catch {
      set({ glossaryStatus: 'error' });
    }
  },

  loadTermWeb: async (force = false) => {
    const { matterId, termWebStatus, termWeb } = get();
    if (!matterId) return;
    if (termWebStatus === 'loading') return;
    if (!force && termWeb.length > 0) return; // cache hit
    set({ termWebStatus: 'loading' });
    try {
      const data = await getSpecTerms(matterId);
      set({ termWeb: data.terms ?? [], termWebStatus: 'idle' });
    } catch {
      set({ termWebStatus: 'error' });
    }
  },

  loadIdfCoverage: async (forceRefresh = false) => {
    const { matterId, idfCoverageStatus } = get();
    if (!matterId) return;
    if (idfCoverageStatus === 'loading') return;
    set({ idfCoverageStatus: 'loading' });
    try {
      const data = await getSpecIdfCoverage(matterId, forceRefresh);
      set({ idfCoverage: data, idfCoverageStatus: 'idle' });
    } catch {
      set({ idfCoverageStatus: 'error' });
    }
  },

  loadLastIdfCoverage: async () => {
    const { matterId, idfCoverage, idfCoverageStatus } = get();
    if (!matterId || idfCoverage || idfCoverageStatus === 'loading') return;
    try {
      const data = await getSpecLastIdfCoverage(matterId);
      const cur = get();
      if (data && cur.matterId === matterId && !cur.idfCoverage) {
        set({ idfCoverage: data });
      }
    } catch {
      /* best-effort: the dock keeps its Run button */
    }
  },

  dismissFigureWarning: (sectionId) => set((s) => {
    if (!(sectionId in s.figureWarnings)) return s;
    const next = { ...s.figureWarnings };
    delete next[sectionId];
    return { figureWarnings: next };
  }),

  dismissFindingWarning: (sectionId) => set((s) => {
    if (!(sectionId in s.findingWarnings)) return s;
    const next = { ...s.findingWarnings };
    delete next[sectionId];
    return { findingWarnings: next };
  }),

  dismissNumeralWarning: (sectionId) => set((s) => {
    if (!(sectionId in s.numeralWarnings)) return s;
    const next = { ...s.numeralWarnings };
    delete next[sectionId];
    return { numeralWarnings: next };
  }),

  dismissAnchorWarning: (sectionId) => set((s) => {
    if (!(sectionId in s.anchorWarnings)) return s;
    const next = { ...s.anchorWarnings };
    delete next[sectionId];
    return { anchorWarnings: next };
  }),

  dismissDiff: (sectionId) => set((s) => {
    if (!(sectionId in s.pendingDiffBySection)) return s;
    const next = { ...s.pendingDiffBySection };
    delete next[sectionId];
    return { pendingDiffBySection: next };
  }),

  revertSectionToSnapshot: async (sectionId) => {
    const { matterId, pendingDiffBySection } = get();
    if (!matterId) {
      return { kind: 'error', message: 'No active matter; cannot revert.' };
    }
    const entry = pendingDiffBySection[sectionId];
    if (!entry || entry.previous.length === 0) {
      return { kind: 'error', message: 'No snapshot available to revert to.' };
    }
    try {
      const res = await postSpecSectionReplaceParagraphs(matterId, sectionId, {
        paragraphs: entry.previous.map((p) => ({
          body_html: p.body_html,
          body_text: p.body_text,
          paragraph_number: p.paragraph_number,
          sequence_num: p.sequence_num,
        })),
        source: 'diff_revert',
      });
      if ((res as SpecStateResponse).locked) {
        return {
          kind: 'locked',
          message: (res as SpecStateResponse).locked_reason
            || 'spec is locked',
        };
      }
      const ok = res as {
        section: SpecSectionRow | null;
        paragraphs: SpecParagraph[];
      };
      set((s) => {
        const nextDiff = { ...s.pendingDiffBySection };
        delete nextDiff[sectionId];
        return {
          paragraphsBySection: {
            ...s.paragraphsBySection,
            [sectionId]: ok.paragraphs,
          },
          sections: s.sections.map((sec) =>
            sec.id === sectionId && ok.section
              ? {
                  ...sec,
                  ...ok.section,
                  paragraph_count: ok.paragraphs.length,
                  ui_state: ok.paragraphs.length > 0 ? 'active' : 'empty',
                  meta_text:
                    ok.paragraphs.length > 0
                      ? `${ok.paragraphs.reduce((n, p) => n + (p.word_count || 0), 0)} w`
                      : 'empty',
                }
              : sec,
          ),
          pendingDiffBySection: nextDiff,
        };
      });
      get().showToast('Reverted to previous version');
      // FIX 3 — coverage live-refresh after revert. The
      // restored content materially changes the audit result;
      // same opt-in gate as generateSection so drafters who
      // haven't engaged with coverage don't get surprise calls.
      if (get().idfCoverage) {
        setTimeout(() => {
          void get().loadIdfCoverage(true);
        }, 2000);
      }
      return { kind: 'ok' };
    } catch (e: unknown) {
      const err = e as {
        response?: { status?: number; data?: { detail?: { message?: string } } };
        message?: string;
      };
      const message =
        err?.response?.data?.detail?.message
        ?? err?.message
        ?? 'Revert failed.';
      return { kind: 'error', message };
    }
  },

  openFigurePopout: (figureId) => set({ popoutFigureId: figureId }),
  closeFigurePopout: () => set({ popoutFigureId: null }),

  // ── Generate Everything (Phase 11 Slice 11D) ─────────────────
  startGenerateEverything: async (args) => {
    const { matterId } = get();
    if (!matterId) return { kind: 'error', message: 'No matter loaded.' };
    set({
      generateEverythingFailures: [],
      generateEverythingCapBlocked: null,
    });
    try {
      const result = await postSpecGenerateEverything(matterId, args);
      set({
        generateEverythingJobId: result.job_id,
        generateEverythingStatus: {
          job_id:            result.job_id,
          status:            'queued',
          progress: {
            total_sections: result.sections_queued.length,
            completed: 0, failed: 0, skipped: 0,
            current_section: null, percent: 0,
          },
          section_results:   [],
          total_duration_ms: null,
          error_message:     null,
          created_at:        new Date().toISOString(),
          completed_at:      null,
        },
      });
      // Spawn polling loop. Bounded: stops when status is
      // terminal OR matter changes OR 5 minutes elapse.
      _startGEPoll(matterId, result.job_id);
      return { kind: 'started' };
    } catch (e: unknown) {
      const ax = e as {
        response?: { status?: number; data?: { detail?: Record<string, unknown> } };
        message?: string;
      };
      const status = ax?.response?.status ?? 0;
      const detail = ax?.response?.data?.detail ?? {};
      if (status === 429
          && (detail as { error_code?: string }).error_code === 'cost_cap_exceeded') {
        set({
          generateEverythingCapBlocked: {
            message: (detail as { message?: string }).message ?? 'Cap reached.',
            reset_at: (detail as { reset_at?: string }).reset_at,
            cap: (detail as { cap?: number }).cap,
            spend: (detail as { current_spend?: number }).current_spend,
          },
        });
        return { kind: 'cap_blocked' };
      }
      const msg =
        (detail as { message?: string }).message
        ?? ax?.message
        ?? 'Failed to start generate-everything.';
      get().showToast(`Generate failed: ${msg}`);
      return { kind: 'error', message: msg };
    }
  },

  cancelGenerateEverything: async () => {
    const { matterId, generateEverythingJobId: jobId } = get();
    if (!matterId || !jobId) return;
    try {
      await cancelSpecGenerateEverything(matterId, jobId);
    } catch (e: unknown) {
      const msg = (e as { message?: string })?.message ?? 'Cancel failed';
      get().showToast(`Cancel failed: ${msg}`);
    }
  },

  dismissGenerateEverythingFailures: () =>
    set({ generateEverythingFailures: [] }),

  hydrateGenerateEverythingRuns: async () => {
    const { matterId } = get();
    if (!matterId) return;
    let runs: SpecGenerateEverythingRun[] = [];
    try {
      const r = await listSpecGenerateEverythingRuns(matterId);
      runs = Array.isArray(r.runs) ? r.runs : [];
    } catch {
      // Soft-fail: history is informational; failure of the list
      // endpoint must never block the rest of the spec page hydrate.
      return;
    }
    set({ generateEverythingRuns: runs });
    // Resume polling for any still-running job. The runs list comes
    // back ordered by created_at DESC, so the first non-terminal row
    // is the live one. Don't replay polling if it's already in flight
    // for the same job (idempotent).
    const live = runs.find(
      (run) => run.status === 'queued' || run.status === 'running',
    );
    if (!live) return;
    // Mount-time staleness gate: a row at status='running' with
    // a created_at older than the threshold means the worker is
    // gone (process killed before the crash-recovery fresh-conn
    // write fired). Resuming polling here would just hammer the
    // status endpoint forever and render the corner card on top
    // of the workbench — the symptom drafters report as
    // "Generate and Examiner don't work". Treat as failed
    // locally; the backend startup sweep (server.py) or the
    // status endpoint's auto-fail (spec.py) will flip the DB
    // row on next visit.
    if (_geJobIsStale(live.status, live.created_at)) {
      set({
        generateEverythingJobId: null,
        generateEverythingStatus: null,
      });
      return;
    }
    const cur = get().generateEverythingJobId;
    if (cur === live.id) return;
    // Seed the status from the list payload so the corner card
    // appears immediately (the first poll tick replaces it with the
    // full section_results in 1.5s).
    set({
      generateEverythingJobId: live.id,
      generateEverythingStatus: {
        job_id:            live.id,
        status:            live.status,
        progress: {
          total_sections: live.progress.total_sections ?? 0,
          completed:      live.progress.completed ?? 0,
          failed:         live.progress.failed ?? 0,
          skipped:        live.progress.skipped ?? 0,
          current_section: live.progress.current_section ?? null,
          percent:        live.progress.percent ?? 0,
        },
        section_results:   [],
        total_duration_ms: live.total_duration_ms,
        error_message:     live.error_message,
        created_at:        live.created_at,
        completed_at:      live.completed_at,
      },
    });
    _startGEPoll(matterId, live.id);
  },

  dismissGenerateEverythingStatusCard: () =>
    set({
      generateEverythingStatus: null,
      generateEverythingJobId: null,
    }),

  anchorFeature: async (mappingId, paragraphId) => {
    const { matterId } = get();
    if (!matterId) {
      return { kind: 'error', message: 'No matter loaded.' };
    }
    try {
      const { mapping } = await patchSpecFeatureMapping(
        matterId, mappingId,
        { anchor_paragraph_id: paragraphId },
      );
      set((s) => ({
        featureMappings: s.featureMappings.map(
          (f) => (f.id === mapping.id ? mapping : f),
        ),
        lastAnchoredParagraphId: paragraphId,
      }));
      // Auto-clear the pulse marker after the animation duration
      // so a future anchor on the same paragraph re-triggers it.
      if (typeof window !== 'undefined') {
        window.setTimeout(() => {
          const cur = useSpecStore.getState().lastAnchoredParagraphId;
          if (cur === paragraphId) {
            useSpecStore.setState({ lastAnchoredParagraphId: null });
          }
        }, 1100);
      }
      return { kind: 'ok', featureTag: mapping.feature_tag };
    } catch (e: unknown) {
      const err = e as {
        response?: { data?: { detail?: { error_code?: string; message?: string } } };
        message?: string;
      };
      const msg =
        err?.response?.data?.detail?.message
        ?? err?.message
        ?? 'anchor failed';
      return { kind: 'error', message: msg };
    }
  },

  unanchorFeature: async (mappingId) => {
    const { matterId } = get();
    if (!matterId) {
      return { kind: 'error', message: 'No matter loaded.' };
    }
    try {
      const { mapping } = await patchSpecFeatureMapping(
        matterId, mappingId,
        { unanchor: true },
      );
      set((s) => ({
        featureMappings: s.featureMappings.map(
          (f) => (f.id === mapping.id ? mapping : f),
        ),
      }));
      return { kind: 'ok', featureTag: mapping.feature_tag };
    } catch (e: unknown) {
      const err = e as {
        response?: { data?: { detail?: { message?: string } } };
        message?: string;
      };
      const msg =
        err?.response?.data?.detail?.message
        ?? err?.message
        ?? 'unanchor failed';
      return { kind: 'error', message: msg };
    }
  },
}));


// ── Generate-Everything polling plumbing (Slice 11D) ─────────────
//
// Lives at module scope so the polling loop survives across
// store re-creations (HMR) and isn't accidentally torn down by
// React's component-mount cycle. Stops when:
//   - status hits a terminal value
//   - matter switches mid-job
//   - 20 minutes elapse (15 min backend stale threshold + 5 min
//     buffer for the stale-check + terminal-write race)
//
// Cap-fix history: the original 5-min cap (commented "matches the
// backend 5-min cap") was inconsistent with the backend's actual
// 15-min stale threshold (_GE_STALE_THRESHOLD_MS below). On a
// legitimate 6-10 minute GE run the FE poll gave up before the
// backend flipped the row to terminal, leaving the running banner
// stuck at the last polled snapshot ("7/10 done, Currently: title"
// forever). The 20-min cap + final-fetch fallback in the
// cap-fired branch closes the gap.


const _GE_POLL_INTERVAL_MS = 5000;
const _GE_POLL_MAX_DURATION_MS = 20 * 60 * 1000;
const _GE_TERMINAL_STATUSES = new Set([
  'completed', 'failed', 'cancelled',
]);

// Mirrors the backend's _GE_STALE_THRESHOLD_MINUTES at
// backend/spec.py. Client-side staleness check is a belt-and-
// braces companion to the server's auto-fail on the next status
// fetch: it lets the polling loop drop a stuck row WITHOUT
// waiting for the backend to observe it, which matters on a
// mount where hydrate finds the stale row but the next poll
// is 5s away.
const _GE_STALE_THRESHOLD_MS = 15 * 60 * 1000;

function _geJobIsStale(
  status: string | undefined,
  createdAt: string | null | undefined,
): boolean {
  if (status !== 'running' && status !== 'queued') return false;
  if (!createdAt) return false;
  const t = Date.parse(createdAt);
  if (Number.isNaN(t)) return false;
  return Date.now() - t > _GE_STALE_THRESHOLD_MS;
}

// BUG-010 — only one polling chain may be live per (matter, job)
// at a time. SpecPage hydrate fires `hydrateGenerateEverythingRuns`
// on every mount (and on same-matter remount) and the tick itself
// re-invokes it on terminal; without this guard each call would
// stack an independent setTimeout chain, producing the observed
// "dozens of /spec/generate-everything/{job} per minute" log
// signature. The guard is module-scoped so it survives store
// re-creates (HMR) the same way `_paragraphSaveTimers` does.
let _activePollKey: string | null = null;


/**
 * Run the terminal-handler housekeeping after a GE job lands a
 * terminal status (completed / failed / cancelled).
 *
 * Factored out of the polling tick body so the cap-fallback path
 * (poll cap fires, final fetch returns a terminal status) can
 * trigger the same UI updates the natural path runs. Without
 * this factoring the cap path would land a terminal status in
 * the store but skip the failures-banner, anchor-warnings,
 * runs-hydrate, eager-section-fetch, and completion-toast — the
 * drafter would see a green "completed" badge but no actual
 * content on the paper until they navigated away and back.
 *
 * Slot release is the caller's responsibility — the natural path
 * releases under ``pollKey`` ownership; the cap path runs
 * outside the tick scope.
 */
/** NB-17 / NB-28 / NB-29 — the GE run's findings, kind → section.id;
 * a section the summary does not list is clean and its entry clears. */
function _findingsBySection(
  cur: { sections: SpecSectionRow[]; findingWarnings: Record<string, SpecGenerationFindings> },
  summary: Record<string, SpecGenerationFindings> | undefined,
): Record<string, SpecGenerationFindings> {
  const next = { ...cur.findingWarnings };
  if (!summary) return next;
  for (const sec of cur.sections) {
    const findings = pickFindings(summary[sec.section_kind]);
    if (findings) {
      next[sec.id] = findings;
    } else {
      delete next[sec.id];
    }
  }
  return next;
}

function _runTerminalHandler(
  matterId: string,
  _jobId: string,
  status: SpecGenerateEverythingStatus,
): void {
  // Surface failed sections as a banner (only failed entries,
  // not skipped — skipped is informational).
  const failures = status.section_results
    .filter((r) => r.status === "failed")
    .map((r) => ({
      section_kind: r.section_kind,
      error_code:   r.error_code,
      message:      r.message,
    }));
  const cur2 = useSpecStore.getState();
  // Anchor-coverage roll-up — keyed by section.id (not
  // section_kind) so the per-section banner component can
  // look its entry up directly. The GE worker emits a map
  // by kind; resolve to id via the current sections list.
  const summary = status.progress.anchor_check_summary || {};
  const nextAnchor = { ...cur2.anchorWarnings };
  for (const [kind, payload] of Object.entries(summary)) {
    const sec = cur2.sections.find((s) => s.section_kind === kind);
    if (!sec) continue;
    const missing = payload?.missing_features || [];
    if (missing.length > 0) {
      nextAnchor[sec.id] = {
        missing,
        dependent: !!payload?.dependent_features_checked,
      };
    } else {
      delete nextAnchor[sec.id];
    }
  }
  // Invented reference signs, resolved kind → section.id the same
  // way. The worker only emits kinds that have one, so anything
  // absent from the map is clean and its banner clears.
  const numeralSummary = status.progress.unregistered_numerals_summary;
  const nextNumeral = { ...cur2.numeralWarnings };
  if (numeralSummary) {
    for (const sec of cur2.sections) {
      const payload = numeralSummary[sec.section_kind];
      const numerals = payload?.numerals || [];
      if (numerals.length > 0) {
        nextNumeral[sec.id] = { numerals, where: payload?.where || {} };
      } else {
        delete nextNumeral[sec.id];
      }
    }
  }
  // Text cut for citing a figure that is not frozen (NB-11), the
  // same kind → section.id mapping; only sections with a cut appear.
  const figureSummary = status.progress.removed_figures_summary;
  const nextFigure = { ...cur2.figureWarnings };
  if (figureSummary) {
    for (const sec of cur2.sections) {
      const payload = figureSummary[sec.section_kind];
      if (payload && payload.figures.length > 0) {
        nextFigure[sec.id] = { figures: payload.figures, excerpts: payload.excerpts || [] };
      } else {
        delete nextFigure[sec.id];
      }
    }
  }
  useSpecStore.setState({
    generateEverythingFailures: failures,
    anchorWarnings: nextAnchor,
    numeralWarnings: nextNumeral,
    figureWarnings: nextFigure,
    findingWarnings: _findingsBySection(cur2, status.progress.generation_findings_summary),
  });
  // Toast a short summary on first terminal arrival.
  const total = status.progress.total_sections;
  const ok = status.progress.completed;
  const failed = status.progress.failed;
  // NB-10 — the dialog promises an examiner review afterwards unless
  // skipped. Nothing ran it (the server-side hook only records a
  // timestamp), so the Examiner view kept "No flags" until the
  // drafter clicked Examiner. Run it here and load its flags.
  const runExaminer = status.status === "completed"
    && ok > 0
    && !status.skip_examiner;
  if (status.status === "completed") {
    cur2.showToast(
      `Generated ${ok} of ${total} sections.`
      + (failed > 0 ? ` ${failed} failed.` : "")
      + (runExaminer ? " Examiner review running in background." : ""),
    );
  } else if (status.status === "cancelled") {
    cur2.showToast("Generate everything cancelled.");
  } else {
    cur2.showToast(
      `Generate everything failed: ${status.error_message ?? "unknown"}`,
    );
  }
  if (runExaminer && cur2.matterId === matterId) {
    void (async () => {
      const r = await useSpecStore.getState().runExaminerReview({
        force: true, reveal: false,
      });
      if (useSpecStore.getState().matterId !== matterId) return;
      if (r.kind === 'ok' || r.kind === 'cached') {
        const n = r.openCount ?? 0;
        useSpecStore.getState().showToast(
          n === 0
            ? "Examiner review finished: no issues found."
            : `Examiner review finished: ${n} issue${n === 1 ? "" : "s"} to check.`,
        );
      } else if (r.kind === 'error') {
        useSpecStore.getState().showToast(
          `Examiner review failed: ${r.message ?? "unknown error"}`,
        );
      }
    })();
  }
  // Refresh the persistent runs list so the just-completed job
  // lands in the history popover. Fire-and-forget — failure is
  // benign (next mount will rehydrate).
  void cur2.hydrateGenerateEverythingRuns();
  // FIX 3 — coverage live-refresh on GE completion. GE
  // typically moves the percentage by 50+ points, so the
  // drafter most needs the audit to refresh here. Gated
  // on idfCoverage already being loaded (mirrors the
  // single-section generate path) to respect BUG-047's
  // opt-in design.
  if (cur2.idfCoverage) {
    setTimeout(() => {
      void useSpecStore.getState().loadIdfCoverage(true);
    }, 2000);
  }
  // BUG-056 + BUG-059 — refresh section status AND eagerly
  // fetch paragraphs for every section that now claims
  // content, so the centre panel renders the freshly
  // generated bodies on the next paint without waiting for
  // the drafter to click into a section. Without the eager
  // fetch the paper used to render blank because PaperEditor's
  // prefetch effect wasn't guaranteed to re-run before the
  // observer painted, leaving "✓ generated" badges paired
  // with an empty body. Fire-and-forget — soft-fail here
  // just means the drafter sees stale state briefly until
  // they navigate, same as the pre-fix behaviour.
  //
  // BUG-S5-008 — also refresh ``section_summary`` +
  // ``spec_document`` from /spec/state so the rail's
  // ``_liveCompletenessPct`` (computed off
  // ``sectionSummary.drafted / (total - 2)``) reflects the
  // post-GE drafted/empty counts. Without this the
  // CompletenessBar shows the pre-GE percentage until the
  // drafter navigates away and back; the section ROWS already
  // refresh (line 2791) so the ✓ generated badges flip
  // immediately, but the bar at the bottom of the rail
  // stayed stale.
  void (async () => {
    try {
      const [freshSections, stateRes] = await Promise.all([
        getSpecSections(matterId),
        getSpecState(matterId),
      ]);
      if (!Array.isArray(freshSections)) return;
      // Fetch every non-auto section's detail in parallel.
      // Auto-generated sections (claims_mirror,
      // reference_signs_list) self-fetch their HTML render
      // via AutoGeneratedSection so we don't pull paragraphs
      // for them — they aren't stored as rows.
      const targets = freshSections.filter(
        (s) => !s.is_auto_generated && (s.paragraph_count ?? 0) > 0,
      );
      const settled = await Promise.all(
        targets.map(async (s) => {
          try {
            const detail = await getSpecSectionDetail(matterId, s.id);
            if (_isLocked(detail)) return null;
            return [s.id, detail.paragraphs] as const;
          } catch {
            return null;
          }
        }),
      );
      const fresh: Record<string, SpecParagraph[]> = {};
      for (const entry of settled) {
        if (entry) fresh[entry[0]] = entry[1];
      }
      useSpecStore.setState((s) => ({
        sections: freshSections,
        paragraphsBySection: fresh,
        // BUG-S5-008 — merge in the refreshed server rollup so the
        // completeness bar + header subtitle reflect the
        // post-GE drafted / empty counts. Locked envelope
        // can't happen here in practice (the GE itself
        // would have failed upstream) but guard anyway.
        sectionSummary: stateRes && !stateRes.locked
          ? (stateRes.section_summary ?? s.sectionSummary)
          : s.sectionSummary,
        specDocument: stateRes && !stateRes.locked
          ? (stateRes.spec_document ?? s.specDocument)
          : s.specDocument,
      }));
    } catch {
      /* swallow — PaperEditor's prefetch effect retries
         with the cleared cache on the next render */
    }
  })();
}


function _startGEPoll(matterId: string, jobId: string): void {
  const pollKey = `${matterId}:${jobId}`;
  if (_activePollKey === pollKey) {
    return; // a poll chain for this (matter, job) is already in flight
  }
  _activePollKey = pollKey;
  const started = Date.now();
  // BUG-S5-008 — mid-GE sectionSummary refresh cadence. Every
  // Nth tick fetches /spec/state so the CompletenessBar +
  // header subtitle climb live as sections are persisted.
  // Tick = 5s (_GE_POLL_INTERVAL_MS), N=3 → ~15s per refresh,
  // which matches the brief's "within 5 seconds of next poll"
  // acceptance criterion within polling resolution while
  // keeping the request rate at a third of the job-status
  // poll. Counter is per-poll-chain — _startGEPoll guards
  // duplicate chains via _activePollKey.
  let _summaryRefreshCounter = 0;

  const tick = async (): Promise<void> => {
    const state = useSpecStore.getState();
    if (state.matterId !== matterId
        || state.generateEverythingJobId !== jobId) {
      // matter switched or job replaced; release the slot so a
      // future _startGEPoll for the new (matter, job) can run.
      if (_activePollKey === pollKey) _activePollKey = null;
      return;
    }
    if (Date.now() - started > _GE_POLL_MAX_DURATION_MS) {
      // Final-fetch fallback before releasing the slot. The most
      // common cause of a stuck running banner past the cap is
      // the worker having written status='completed' to the DB
      // in the window between the last poll and this cap firing.
      // One more best-effort fetch picks that up; falling all the
      // way through to "terminal handler ran" closes the loop
      // cleanly. If the fetch returns still-running, synthesise
      // a terminal failed status with a clear timeout message so
      // the UI flips from sticky-running to terminal — drafter
      // knows to refresh rather than waiting on a dead poll.
      try {
        const finalStatus = await getSpecGenerateEverythingStatus(
          matterId, jobId,
        );
        const curFinal = useSpecStore.getState();
        if (curFinal.generateEverythingJobId === jobId) {
          if (_GE_TERMINAL_STATUSES.has(finalStatus.status)) {
            useSpecStore.setState({
              generateEverythingStatus: finalStatus,
            });
            // Trigger the same terminal-handler housekeeping
            // (failures banner, anchor warnings, runs hydrate,
            // eager section/paragraph refetch) the normal path
            // runs. Cleanest is one more synthetic tick — the
            // cap check is past us; the terminal branch fires
            // on the synthesised state and releases the slot.
            void _runTerminalHandler(matterId, jobId, finalStatus);
          } else {
            useSpecStore.setState({
              generateEverythingStatus: {
                ...finalStatus,
                status: "failed",
                error_message: (
                  finalStatus.error_message
                  ?? "Polling stopped after 20 minutes without "
                  + "a terminal status. The job may have "
                  + "completed in the background — refresh "
                  + "the page to check, or open the recent-runs "
                  + "popover."
                ),
              },
            });
          }
        }
      } catch {
        // Network blip on the final fetch — leave the running
        // banner in place; the drafter can refresh manually.
        // No console warning so we don't spam the log on a
        // dead-network tab.
      }
      if (_activePollKey === pollKey) _activePollKey = null;
      return;
    }
    try {
      const status = await getSpecGenerateEverythingStatus(matterId, jobId);
      const cur = useSpecStore.getState();
      if (cur.generateEverythingJobId !== jobId) {
        if (_activePollKey === pollKey) _activePollKey = null;
        return;
      }
      // Client-side staleness fallback. The backend's status
      // endpoint also auto-fails stale rows (spec.py), but a
      // racing window between two polls could land us on a
      // row the server hasn't yet processed. Synthesise a
      // failed-status patch locally so the loop drops cleanly
      // without one more roundtrip. createdAt isn't refetched
      // — it's the row's immutable timestamp, so using the
      // value the server just returned is safe.
      if (_geJobIsStale(status.status, status.created_at)) {
        useSpecStore.setState({
          generateEverythingStatus: {
            ...status,
            status: 'failed',
            error_message:
              status.error_message
              ?? 'Generation timed out (no progress for 15+ minutes).',
          },
        });
        if (_activePollKey === pollKey) _activePollKey = null;
        return;
      }
      useSpecStore.setState({
        generateEverythingStatus: status,
      });
      if (_GE_TERMINAL_STATUSES.has(status.status)) {
        _runTerminalHandler(matterId, jobId, status);
        // Terminal — release the polling slot for this (matter, job).
        if (_activePollKey === pollKey) _activePollKey = null;
        return;
      }
      // BUG-S5-008 — mid-flight sectionSummary refresh. Every
      // third tick (~15s) fetch /spec/state so the rail's
      // CompletenessBar climbs live as worker-persisted
      // sections drop into the database. Fire-and-forget;
      // soft-fail leaves the previous summary in place.
      _summaryRefreshCounter += 1;
      if (_summaryRefreshCounter % 3 === 0) {
        void (async () => {
          try {
            const stateRes = await getSpecState(matterId);
            const curRef = useSpecStore.getState();
            // Guard — drafter may have switched matters between
            // the fetch and the apply.
            if (curRef.matterId !== matterId
                || curRef.generateEverythingJobId !== jobId) return;
            if (stateRes.locked) return;
            useSpecStore.setState((s) => ({
              sectionSummary:
                stateRes.section_summary ?? s.sectionSummary,
              specDocument:
                stateRes.spec_document ?? s.specDocument,
            }));
          } catch {
            /* best-effort */
          }
        })();
      }
    } catch {
      // Soft-fail this tick; keep polling.
    }
    window.setTimeout(() => { void tick(); }, _GE_POLL_INTERVAL_MS);
  };

  window.setTimeout(() => { void tick(); }, _GE_POLL_INTERVAL_MS);
}


// ── Module-scoped paragraph save plumbing (Slice 5E) ─────────────
// Per-paragraph debounce timers + the actual PATCH dispatcher.
// Lives at module scope so it survives store re-creates (HMR
// safety) and so the same dispatcher is reused across paragraphs.

const _paragraphSaveTimers: Record<string, ReturnType<typeof setTimeout>> = {};

async function _persistParagraphNow(
  paragraphId: string,
  sectionId: string,
  bodyHtml: string,
  bodyText: string,
): Promise<void> {
  const { matterId } = useSpecStore.getState();
  if (!matterId) return;
  useSpecStore.setState((s) => ({
    paragraphSaveStatus: 'saving',
    pendingSaveCount: s.pendingSaveCount + 1,
  }));
  try {
    await patchSpecParagraph(matterId, sectionId, paragraphId, {
      body_html: bodyHtml,
      body_text: bodyText,
    });
    useSpecStore.setState((s) => ({
      pendingSaveCount: Math.max(0, s.pendingSaveCount - 1),
      paragraphSaveStatus:
        Math.max(0, s.pendingSaveCount - 1) === 0 ? 'idle' : 'saving',
      paragraphSaveError: null,
      lastSavedAt: new Date().toISOString(),
      // Successful save → if we were offline, opportunistically
      // attempt to drain the queue (next paragraph save will see
      // pendingOfflineCount drop).
    }));
    // Best-effort drain after every successful save.
    void useSpecStore.getState().flushOfflineQueue();
  } catch (e: unknown) {
    const err = e as {
      response?: { status?: number; data?: { detail?: { message?: string } } };
      message?: string;
      code?: string;
    };
    const msg =
      err?.response?.data?.detail?.message ??
      err?.message ??
      'Auto-save failed.';
    // Phase 6 Slice 6F — network failure → enqueue to IDB +
    // surface offline mode. "Network failure" = no err.response
    // (axios sets response on server errors; absent ⇒ network).
    const isNetworkError = !err?.response;
    if (isNetworkError) {
      try {
        const queue = await enqueueOfflineEntry({
          matterId, sectionId, paragraphId,
          bodyHtml, bodyText,
          queuedAt: new Date().toISOString(),
        });
        useSpecStore.setState((s) => ({
          pendingSaveCount: Math.max(0, s.pendingSaveCount - 1),
          paragraphSaveStatus: 'error',
          paragraphSaveError: msg,
          offlineMode: true,
          pendingOfflineCount: Object.keys(queue).length,
        }));
      } catch {
        // IDB itself failed — fall through to error state.
        useSpecStore.setState((s) => ({
          pendingSaveCount: Math.max(0, s.pendingSaveCount - 1),
          paragraphSaveStatus: 'error',
          paragraphSaveError: msg,
        }));
      }
    } else {
      useSpecStore.setState((s) => ({
        pendingSaveCount: Math.max(0, s.pendingSaveCount - 1),
        paragraphSaveStatus: 'error',
        paragraphSaveError: msg,
      }));
    }
  }
}


// ── Module-scoped paragraph mutation plumbing (Slice 6D) ─────────

function _findSectionIdForParagraph(
  state: ReturnType<typeof useSpecStore.getState>,
  paragraphId: string,
): string | null {
  const map = state.paragraphsBySection;
  for (const sectionId of Object.keys(map)) {
    const list: SpecParagraph[] = map[sectionId] ?? [];
    if (list.some((p) => p.id === paragraphId)) return sectionId;
  }
  return null;
}

/** Shared expand/tighten runner. Sets the in-flight status, fires
 * the API call, patches the paragraph in the local cache on success,
 * and surfaces locked / error outcomes via SpecParagraphMutationOutcome. */
async function _runParagraphMutation(
  status: 'expanding' | 'tightening',
  paragraphId: string,
  sectionId: string,
  call: (matterId: string) => Promise<
    SpecParagraphExpandResponse | SpecParagraphTightenResponse | SpecStateResponse
  >,
  charDiffOf: (
    resp: SpecParagraphExpandResponse | SpecParagraphTightenResponse,
  ) => number,
): Promise<SpecParagraphMutationOutcome> {
  const { matterId } = useSpecStore.getState();
  if (!matterId) {
    return { kind: 'error', message: 'No active matter.' };
  }
  useSpecStore.setState({
    paragraphMutationStatus: status,
    paragraphMutationError: null,
    paragraphMutationTargetId: paragraphId,
  });
  try {
    const res = await call(matterId);
    if ((res as SpecStateResponse).locked) {
      const reason =
        (res as SpecStateResponse).locked_reason || 'drawings_not_frozen';
      useSpecStore.setState({
        paragraphMutationStatus: 'idle',
        paragraphMutationError: null,
        paragraphMutationTargetId: null,
      });
      return { kind: 'locked', reason };
    }
    const ok = res as SpecParagraphExpandResponse | SpecParagraphTightenResponse;
    // Patch the paragraph in the local cache.
    useSpecStore.setState((s) => {
      const next = { ...s.paragraphsBySection };
      const list = (next[sectionId] ?? []).map((p) =>
        p.id === paragraphId ? { ...p, ...ok.paragraph } : p,
      );
      next[sectionId] = list;
      // BUG-015 — server-side content replacement; bump the
      // version counter so ParagraphBlock's imperative
      // innerHTML effect re-runs and the new body reaches
      // the DOM. updateParagraphLocalAndQueueSave does NOT
      // bump this — local typing keeps the cursor stable.
      const nextVersion = {
        ...s.paragraphServerVersion,
        [paragraphId]: (s.paragraphServerVersion[paragraphId] ?? 0) + 1,
      };
      return {
        paragraphsBySection: next,
        paragraphServerVersion: nextVersion,
        paragraphMutationStatus: 'idle',
        paragraphMutationError: null,
        paragraphMutationTargetId: null,
        lastSavedAt: new Date().toISOString(),
      };
    });
    return {
      kind: 'ok',
      paragraph: ok.paragraph,
      charDiff: charDiffOf(ok),
    };
  } catch (e: unknown) {
    const err = e as {
      response?: {
        data?: { detail?: { error_code?: string; message?: string } };
      };
      message?: string;
    };
    const detail = err?.response?.data?.detail ?? {};
    const message = detail.message ?? err?.message ?? 'Mutation failed.';
    useSpecStore.setState({
      paragraphMutationStatus: 'error',
      paragraphMutationError: message,
      paragraphMutationTargetId: null,
    });
    return { kind: 'error', message, errorCode: detail.error_code };
  }
}
